import { relations } from 'drizzle-orm';
import {
  bigint,
  boolean,
  datetime,
  decimal,
  foreignKey,
  index,
  int,
  json,
  longtext,
  mysqlEnum,
  mysqlTable,
  primaryKey,
  text,
  timestamp,
  uniqueIndex,
  varchar,
} from 'drizzle-orm/mysql-core';
import { createInsertSchema } from 'drizzle-zod';
import { z } from 'zod';

import {
  buildLocaleFallbackChain,
  defaultLocale,
  normalizeSupportedLocale,
  supportedLocales,
} from './locales';
import {
  combinePhoneNumber,
  isPhoneValueEmptyLike,
  isPhoneValueWithCountryCode,
  PHONE_NUMBER_ERROR_MESSAGE,
} from './phone';

export type { SupportedLocale } from './locales';
export { buildLocaleFallbackChain, defaultLocale, normalizeSupportedLocale, supportedLocales };

export const userRoles = [
  'SUPER_ADMIN',
  'ADMIN',
  'MANAGER',
  'SALESMAN',
  'SOCIAL_MEDIA',
  'PRODUCT_EDITOR',
] as const;
export const userCapabilities = ['SOCIAL_MEDIA', 'PRODUCT_EDITOR'] as const;
export const companyIndustries = [
  'Real Estate',
  'Education',
  'Healthcare',
  'Manufacturing',
  'Retail',
  'IT / Software',
  'Finance',
  'Marketing',
  'Automobile',
  'Construction',
  'Travel',
  'Hospitality',
  'Legal',
  'Consulting',
  'E-commerce',
  'Logistics',
  'General',
  'Other',
] as const;
export const companyIndustryTranslationKeys: Record<(typeof companyIndustries)[number], string> = {
  'Real Estate': 'realEstate',
  Education: 'education',
  Healthcare: 'healthcare',
  Manufacturing: 'manufacturing',
  Retail: 'retail',
  'IT / Software': 'itSoftware',
  Finance: 'finance',
  Marketing: 'marketing',
  Automobile: 'automobile',
  Construction: 'construction',
  Travel: 'travel',
  Hospitality: 'hospitality',
  Legal: 'legal',
  Consulting: 'consulting',
  'E-commerce': 'ecommerce',
  Logistics: 'logistics',
  General: 'general',
  Other: 'other',
};
export const subscriptionStatuses = ['pending', 'active', 'trial', 'expired', 'cancelled'] as const;
export const leadStatuses = ['NEW', 'HOT', 'WARM', 'COLD', 'CLOSED_WON', 'CLOSED_LOST'] as const;
export const openLeadStatuses = ['NEW', 'HOT', 'WARM', 'COLD'] as const;
export const closedLeadStatuses = ['CLOSED_WON', 'CLOSED_LOST'] as const;
export const leadPipelineStages = [
  'LEAD_CREATED',
  'CONTACTED',
  'FOLLOWUP_SCHEDULED',
  'QUOTATION_SENT',
  'CONVERSION',
  'LOST',
] as const;
export const leadFollowupPurposes = [
  'GENERAL_CALL',
  'SITE_VISIT',
  'DEMO_MEETING',
  'PROPOSAL_REVIEW',
  'PAYMENT_DISCUSSION',
  'DOCUMENT_COLLECTION',
  'CUSTOM',
] as const;
export const leadConversionStatuses = ['converted', 'lost', 'open'] as const;
export const leadStageHistoryEventTypes = ['created', 'transition'] as const;
export type LeadConversionStatus = (typeof leadConversionStatuses)[number];
export type LeadPipelineStage = (typeof leadPipelineStages)[number];
export type LeadFollowupPurpose = (typeof leadFollowupPurposes)[number];

export const LEAD_FOLLOWUP_MAX_TIMESTAMP = new Date('2038-01-19T03:14:07.000Z');
export const LEAD_FOLLOWUP_MAX_DATE_MESSAGE =
  'Next follow-up date must be on or before January 19, 2038.';
export const LEAD_FOLLOWUP_DEFAULT_DURATION_MINUTES = 5;
export const LEAD_FOLLOWUP_MIN_DURATION_MINUTES = 5;
export const LEAD_FOLLOWUP_MAX_DURATION_MINUTES = 240;
export const activityTypes = ['CALL', 'EMAIL', 'MEETING', 'NOTE'] as const;
export const activitySources = ['manual', 'system', 'automation', 'integration'] as const;
export const notificationTypes = ['ALERT', 'REMINDER', 'ASSIGNMENT'] as const;
export const notificationChannels = ['in_app', 'whatsapp', 'email'] as const;
export const notificationJobStatuses = [
  'pending',
  'scheduled',
  'processing',
  'sent',
  'failed',
  'cancelled',
  'skipped',
] as const;
export const reportScopes = ['ADMIN', 'MANAGER'] as const;
export const settingsFieldTypes = [
  'text',
  'textarea',
  'number',
  'boolean',
  'select',
  'multiselect',
  'json',
  'color',
  'time',
  'image',
  'email',
  'phone',
] as const;
export const settingsValueScopes = ['platform', 'company'] as const;
export const passwordResetRequestStatuses = [
  'pending',
  'verified',
  'consumed',
  'expired',
  'cancelled',
] as const;
export const broadcastCampaignStatuses = [
  'draft',
  'scheduled',
  'sending',
  'completed',
  'failed',
] as const;
export const broadcastRecipientStatuses = ['pending', 'sent', 'failed', 'replied'] as const;
export const messageDirections = ['inbound', 'outbound'] as const;
export const conversationChannels = ['whatsapp', 'facebook', 'instagram', 'threads'] as const;
export const socialAccountPlatforms = [
  'facebook',
  'instagram',
  'threads',
  'google_business',
  'youtube',
] as const;
export type SocialAccountPlatform = (typeof socialAccountPlatforms)[number];
export const socialInteractionTypes = ['dm', 'comment', 'reply'] as const;
export const conversationMessageTypes = [
  'text',
  'image',
  'video',
  'document',
  'audio',
  'sticker',
  'location',
  'contacts',
  'interactive',
  'button',
  'order',
  'reaction',
  'system',
  'unsupported',
  'edit',
  'revoke',
  'unknown',
] as const;
export const messageDeliveryStatuses = ['sent', 'delivered', 'read', 'failed'] as const;
export const conversationAiAutomationJobStatuses = [
  'pending',
  'processing',
  'sending',
  'sent',
  'skipped',
  'failed',
] as const;
export const aiSocialPostGenerationStatuses = [
  'scheduled',
  'generating',
  'ready',
  'failed',
] as const;
export const aiSocialPostApprovalStatuses = [
  'not_required',
  'pending',
  'approved',
  'rejected',
] as const;
export const aiSocialPostPublishStatuses = [
  'not_requested',
  'queued',
  'publishing',
  'published',
  'partially_published',
  'failed',
] as const;
export const aiSocialPostMediaTypes = ['image', 'video'] as const;
export const aiGenerationOperationTypes = [
  'caption',
  'image',
  'image_transform',
  'video',
  'video_extension',
  'tts',
  'audio_mix',
] as const;
export const aiGenerationOperationStatuses = [
  'pending',
  'started',
  'processing',
  'completed',
  'failed',
  'cancelled',
  'recovered',
] as const;
export type AiGenerationOperationType = (typeof aiGenerationOperationTypes)[number];
export type AiGenerationOperationStatus = (typeof aiGenerationOperationStatuses)[number];
export const transactionTypes = ['CREDIT', 'DEBIT', 'REFUND', 'RECHARGE'] as const;
export const debitTypes = [
  'SUBSCRIPTION_BONUS',
  'MAP',
  'WHATSAPP',
  'GEMINI',
  'RECHARGE',
  'ADJUSTMENT',
  'WELCOME_BONUS',
  'WHATSAPP_AUTO_REPLY',
  'SOCIAL_AI_CAPTION',
  'SOCIAL_AI_IMAGE',
  'SOCIAL_AI_VIDEO',
  'SOCIAL_AI_TTS',
  'FACEBOOK_AUTO_REPLY',
  'INSTAGRAM_AUTO_REPLY',
  'THREADS_AUTO_REPLY',
] as const;
export const sourceTypes = ['USER_ACTION', 'CRON', 'SYSTEM'] as const;
export const serviceKeys = [
  'MAP',
  'WHATSAPP',
  'GEMINI',
  'WHATSAPP_AUTO_REPLY',
  'SOCIAL_AI_CAPTION',
  'SOCIAL_AI_IMAGE',
  'SOCIAL_AI_VIDEO',
  'SOCIAL_AI_TTS',
] as const;
export const walletRechargeStatuses = ['CREATED', 'PAID', 'FAILED', 'CANCELLED'] as const;
export const productTypes = ['product', 'service'] as const;
export const productStatuses = ['active', 'draft', 'archived'] as const;
export const productPriceModes = ['fixed', 'dynamic'] as const;
export const productAvailabilityStatuses = [
  'in_stock',
  'out_of_stock',
  'preorder',
  'available',
] as const;

export const roleEnum = mysqlEnum('role', userRoles);
export const userSubscriptionStatusEnum = mysqlEnum('subscription_status', subscriptionStatuses);
export const leadStatusEnum = mysqlEnum('status', leadStatuses);
export const leadPipelineStageEnum = mysqlEnum('pipeline_stage', leadPipelineStages);
const leadStageHistoryEventTypeEnum = mysqlEnum('event_type', leadStageHistoryEventTypes);
const leadStageHistoryPreviousPipelineStageEnum = mysqlEnum(
  'previous_pipeline_stage',
  leadPipelineStages
);
const leadStageHistoryNextPipelineStageEnum = mysqlEnum('next_pipeline_stage', leadPipelineStages);
const leadStageHistoryPreviousStatusEnum = mysqlEnum('previous_status', leadStatuses);
const leadStageHistoryNextStatusEnum = mysqlEnum('next_status', leadStatuses);
export const subscriptionRecordStatusEnum = mysqlEnum('status', subscriptionStatuses);
export const activityTypeEnum = mysqlEnum('activity_type', activityTypes);
export const notificationTypeEnum = mysqlEnum('notification_type', notificationTypes);
export const notificationChannelEnum = mysqlEnum('notification_channel', notificationChannels);
export const notificationJobStatusEnum = mysqlEnum(
  'notification_job_status',
  notificationJobStatuses
);
export const settingsFieldTypeEnum = mysqlEnum('settings_field_type', settingsFieldTypes);
export const settingsValueScopeEnum = mysqlEnum('scope', settingsValueScopes);
export const passwordResetRequestStatusEnum = mysqlEnum(
  'password_reset_request_status',
  passwordResetRequestStatuses
);
export const broadcastCampaignStatusEnum = mysqlEnum(
  'broadcast_campaign_status',
  broadcastCampaignStatuses
);
export const broadcastRecipientStatusEnum = mysqlEnum(
  'broadcast_recipient_status',
  broadcastRecipientStatuses
);
export const messageDirectionEnum = mysqlEnum('message_direction', messageDirections);
export const conversationChannelEnum = mysqlEnum('conversation_channel', conversationChannels);
export const socialPlatformEnum = mysqlEnum('platform', ['whatsapp', ...socialAccountPlatforms]);
export const socialInteractionTypeEnum = mysqlEnum(
  'social_interaction_type',
  socialInteractionTypes
);
export const conversationMessageTypeEnum = mysqlEnum('message_type', conversationMessageTypes);
export const messageDeliveryStatusEnum = mysqlEnum('delivery_status', messageDeliveryStatuses);
export const conversationAiAutomationJobStatusEnum = mysqlEnum(
  'status',
  conversationAiAutomationJobStatuses
);
export const aiSocialPostGenerationStatusEnum = mysqlEnum(
  'generation_status',
  aiSocialPostGenerationStatuses
);
export const aiSocialPostApprovalStatusEnum = mysqlEnum(
  'approval_status',
  aiSocialPostApprovalStatuses
);
export const aiSocialPostPublishStatusEnum = mysqlEnum(
  'publish_status',
  aiSocialPostPublishStatuses
);
export const aiSocialPostMediaTypeEnum = mysqlEnum('media_type', aiSocialPostMediaTypes);
export const aiGenerationOperationTypeEnum = mysqlEnum(
  'operation_type',
  aiGenerationOperationTypes
);
export const aiGenerationOperationStatusEnum = mysqlEnum('status', aiGenerationOperationStatuses);
export const transactionTypeEnum = mysqlEnum('wallet_transaction_type', transactionTypes);
export const debitTypeEnum = mysqlEnum('wallet_debit_type', debitTypes);
export const sourceTypeEnum = mysqlEnum('wallet_source_type', sourceTypes);
export const serviceKeyEnum = mysqlEnum('service_key', serviceKeys);
export const walletRechargeStatusEnum = mysqlEnum('wallet_recharge_status', walletRechargeStatuses);
export const productTypeEnum = mysqlEnum('type', productTypes);
export const productStatusEnum = mysqlEnum('status', productStatuses);
export const productPriceModeEnum = mysqlEnum('price_mode', productPriceModes);
export const productAvailabilityStatusEnum = mysqlEnum('availability', productAvailabilityStatuses);

export const users = mysqlTable(
  'users',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    username: varchar('username', { length: 255 }).notNull().unique(),
    password: text('password').notNull(),
    name: text('name').notNull(),
    email: varchar('email', { length: 255 }).notNull(),
    phone: varchar('phone', { length: 50 }).notNull(),
    preferredLocale: varchar('preferred_locale', { length: 20 }),
    timeZone: varchar('time_zone', { length: 64 }),
    role: roleEnum.default('ADMIN').notNull(),
    roles: json('roles').$type<(typeof userCapabilities)[number][] | null>(),
    adminId: bigint('admin_id', { mode: 'number' }),
    managerId: bigint('manager_id', { mode: 'number' }),
    subscriptionStatus: userSubscriptionStatusEnum.default('pending'),
    razorpayOrderId: text('razorpay_order_id'),
    razorpayPaymentId: text('razorpay_payment_id'),
    subscriptionStartDate: datetime('subscription_start_date'),
    subscriptionExpiryDate: datetime('subscription_expiry_date'),
    subscriptionAmount: int('subscription_amount'),
    trialStartDate: datetime('trial_start_date'),
    trialExpiryDate: datetime('trial_expiry_date'),
    isTrialUsed: boolean('is_trial_used').default(false).notNull(),
    isTerminated: boolean('is_terminated').default(false).notNull(),
    walletBalance: decimal('wallet_balance', {
      precision: 12,
      scale: 2,
    })
      .default('0.00')
      .notNull(),
    walletLastUpdatedAt: timestamp('wallet_last_updated_at').defaultNow().onUpdateNow(),
    createdAt: timestamp('created_at').defaultNow(),
  },
  table => ({
    emailIdx: uniqueIndex('users_email_idx').on(table.email),
    phoneIdx: uniqueIndex('users_phone_idx').on(table.phone),
  })
);

export type LeadProductSnapshot = {
  name: string;
  sku: string | null;
  variant: string | null;
  price: number;
  currency: string;
};

export const products = mysqlTable(
  'products',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    name: text('name').notNull(),
    description: text('description'),
    price: int('price').default(0), // Price in paise
    priceMode: productPriceModeEnum.default('fixed').notNull(),
    type: productTypeEnum.default('product').notNull(),
    status: productStatusEnum.default('active').notNull(),
    archivedAt: timestamp('archived_at'),
    archivedBy: bigint('archived_by', { mode: 'number' }).references(() => users.id, {
      onDelete: 'set null',
    }),
    sku: varchar('sku', { length: 100 }),
    brand: varchar('brand', { length: 160 }),
    category: varchar('category', { length: 160 }),
    currency: varchar('currency', { length: 3 }).default('INR').notNull(),
    salePrice: int('sale_price'),
    // Optional approved price band the AI may use while negotiating. Values remain in paise.
    negotiationMinPrice: int('negotiation_min_price'),
    negotiationMaxPrice: int('negotiation_max_price'),
    availability: productAvailabilityStatusEnum.default('in_stock').notNull(),
    inventoryQuantity: int('inventory_quantity'),
    productUrl: varchar('product_url', { length: 2048 }),
    // Remote product identifiers are intentionally separate from the legacy SKU and are only
    // written by the product-sync integration.
    externalId: varchar('external_id', { length: 191 }),
    parentProductId: bigint('parent_product_id', { mode: 'number' }),
    adminId: bigint('admin_id', { mode: 'number' }), // Which admin owns this product
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow(),
  },
  table => ({
    adminStatusIdx: index('products_admin_status_idx').on(table.adminId, table.status),
    adminSkuIdx: uniqueIndex('products_admin_sku_idx').on(table.adminId, table.sku),
    adminExternalIdIdx: uniqueIndex('products_admin_external_id_idx').on(
      table.adminId,
      table.externalId
    ),
    parentProductIdx: index('products_parent_product_idx').on(table.parentProductId),
    parentProductFk: foreignKey({
      columns: [table.parentProductId],
      foreignColumns: [table.id],
      name: 'products_parent_product_fk',
    }).onDelete('restrict'),
  })
);

export const productMedia = mysqlTable(
  'product_media',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    productId: bigint('product_id', { mode: 'number' })
      .notNull()
      .references(() => products.id, { onDelete: 'cascade' }),
    adminId: bigint('admin_id', { mode: 'number' })
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    storageKey: varchar('storage_key', { length: 768 }).notNull(),
    fileName: varchar('file_name', { length: 255 }).notNull(),
    mimeType: varchar('mime_type', { length: 100 }).notNull(),
    sizeBytes: int('size_bytes').notNull(),
    altText: varchar('alt_text', { length: 500 }),
    externalId: varchar('external_id', { length: 191 }),
    sourceUrl: varchar('source_url', { length: 2048 }),
    sortOrder: int('sort_order').default(0).notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => ({
    storageKeyIdx: uniqueIndex('product_media_storage_key_idx').on(table.storageKey),
    productSortIdx: index('product_media_product_sort_idx').on(table.productId, table.sortOrder),
    adminProductIdx: index('product_media_admin_product_idx').on(table.adminId, table.productId),
    productExternalIdIdx: uniqueIndex('product_media_product_external_id_idx').on(
      table.productId,
      table.externalId
    ),
  })
);

export const productIntegrationKeys = mysqlTable(
  'product_integration_keys',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    companyId: bigint('company_id', { mode: 'number' })
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    keyHash: varchar('key_hash', { length: 64 }).notNull(),
    keyPrefix: varchar('key_prefix', { length: 24 }).notNull(),
    createdBy: bigint('created_by', { mode: 'number' }).references(() => users.id, {
      onDelete: 'set null',
    }),
    lastUsedAt: timestamp('last_used_at'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => ({
    companyIdx: uniqueIndex('product_integration_keys_company_idx').on(table.companyId),
  })
);

export const leadCategories = mysqlTable(
  'lead_categories',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    adminId: bigint('admin_id', { mode: 'number' })
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    name: varchar('name', { length: 100 }).notNull(),
    isDefault: boolean('is_default').default(false).notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    adminNameIdx: uniqueIndex('lead_categories_admin_name_idx').on(table.adminId, table.name),
    adminDefaultIdx: index('lead_categories_admin_default_idx').on(table.adminId, table.isDefault),
  })
);

export const leads = mysqlTable(
  'leads',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    leadCode: varchar('lead_id', { length: 32 }),
    name: text('name').notNull(),
    address: text('address'),
    company: text('company'),
    email: text('email').notNull(),
    phone: text('phone').notNull(),
    productId: bigint('product_id', { mode: 'number' }).references(() => products.id, {
      onDelete: 'restrict',
    }),
    productSnapshot: json('product_snapshot').$type<LeadProductSnapshot>(),
    // Internal reservation marker; never accepted from lead mutation requests.
    inventoryConsumedProductId: bigint('inventory_consumed_product_id', {
      mode: 'number',
    }).references(() => products.id, { onDelete: 'restrict' }),
    categoryId: bigint('category_id', { mode: 'number' }).references(() => leadCategories.id, {
      onDelete: 'set null',
    }),
    value: int('value').default(0), // Estimated deal value in paise
    nextFollowupDate: timestamp('next_followup_date'),
    nextFollowupPurpose: varchar('next_followup_purpose', { length: 64 }),
    nextFollowupDurationMinutes: int('next_followup_duration_minutes').default(
      LEAD_FOLLOWUP_DEFAULT_DURATION_MINUTES
    ),
    notes: text('notes'),
    status: leadStatusEnum.default('NEW').notNull(),
    pipelineStage: leadPipelineStageEnum.default('LEAD_CREATED').notNull(),
    source: text('source').notNull(),
    googleWorkspaceImported: boolean('google_workspace_imported').default(false).notNull(),
    adminId: bigint('admin_id', { mode: 'number' }), // Make optional for migration
    assignedUserId: bigint('assigned_user_id', { mode: 'number' }).references(() => users.id),
    createdBy: bigint('created_by', { mode: 'number' }).references(() => users.id),
    createdAt: timestamp('created_at').defaultNow(),
    updatedAt: timestamp('updated_at').defaultNow(),
  },
  table => ({
    leadCodeIdx: uniqueIndex('leads_lead_id_idx').on(table.leadCode),
    adminCreatedAtIdx: index('leads_admin_created_at_idx').on(table.adminId, table.createdAt),
    adminAssigneeCreatedAtIdx: index('leads_admin_assignee_created_at_idx').on(
      table.adminId,
      table.assignedUserId,
      table.createdAt
    ),
    adminCreatorCreatedAtIdx: index('leads_admin_creator_created_at_idx').on(
      table.adminId,
      table.createdBy,
      table.createdAt
    ),
    adminFollowupIdx: index('leads_admin_followup_idx').on(table.adminId, table.nextFollowupDate),
  })
);

// Attachment history keeps replaced products and their quotations protected from deletion.
export const leadProductHistory = mysqlTable('lead_product_history', {
  id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
  leadId: bigint('lead_id', { mode: 'number' })
    .notNull()
    .references(() => leads.id, { onDelete: 'cascade' }),
  productId: bigint('product_id', { mode: 'number' })
    .notNull()
    .references(() => products.id, { onDelete: 'restrict' }),
  snapshot: json('snapshot').$type<LeadProductSnapshot>().notNull(),
  changedBy: bigint('changed_by', { mode: 'number' }).references(() => users.id, {
    onDelete: 'set null',
  }),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

// Audit events retain the product ID and name even after an unreferenced product is deleted.
export const productLifecycleEvents = mysqlTable('product_lifecycle_events', {
  id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
  productId: bigint('product_id', { mode: 'number' }).notNull(),
  adminId: bigint('admin_id', { mode: 'number' }).notNull(),
  actorId: bigint('actor_id', { mode: 'number' }).references(() => users.id, {
    onDelete: 'set null',
  }),
  action: mysqlEnum('action', ['archive', 'restore', 'delete']).notNull(),
  productName: text('product_name').notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
});

export const activities = mysqlTable(
  'activities',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    leadId: bigint('lead_id', { mode: 'number' })
      .references(() => leads.id)
      .notNull(),
    adminId: bigint('admin_id', { mode: 'number' }), // Make optional for migration
    userId: bigint('user_id', { mode: 'number' })
      .references(() => users.id)
      .notNull(),
    type: text('type', { enum: activityTypes }).notNull(),
    content: text('content').notNull(),
    scheduledAt: timestamp('scheduled_at'),
    isCompleted: boolean('is_completed').default(false).notNull(),
    createdAt: timestamp('created_at').defaultNow(),
    source: text('source', { enum: activitySources }),
    providerMessageId: varchar('provider_message_id', { length: 191 }).unique(
      'activities_provider_message_id_idx'
    ),
  },
  table => ({
    adminCreatedAtIdx: index('activities_admin_created_at_idx').on(table.adminId, table.createdAt),
    leadCreatedAtIdx: index('activities_lead_created_at_idx').on(table.leadId, table.createdAt),
  })
);

export const notifications = mysqlTable('notifications', {
  id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
  userId: bigint('user_id', { mode: 'number' })
    .references(() => users.id)
    .notNull(),
  message: text('message').notNull(),
  targetUrl: varchar('target_url', { length: 1024 }),
  type: text('type', { enum: notificationTypes }).notNull(),
  isRead: boolean('is_read').default(false).notNull(),
  createdAt: timestamp('created_at').defaultNow(),
});

export const sessions = mysqlTable(
  'sessions',
  {
    sessionId: varchar('session_id', { length: 128 }).primaryKey(),
    expires: int('expires', { unsigned: true }).notNull(),
    data: longtext('data'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    expiresIdx: index('sessions_expires_idx').on(table.expires),
  })
);

export const pushSubscriptions = mysqlTable(
  'push_subscriptions',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    userId: bigint('user_id', { mode: 'number' })
      .references(() => users.id)
      .notNull(),
    endpointHash: varchar('endpoint_hash', { length: 191 }).notNull(),
    endpoint: text('endpoint').notNull(),
    p256dh: text('p256dh').notNull(),
    auth: text('auth').notNull(),
    expirationTime: bigint('expiration_time', { mode: 'number' }),
    userAgent: text('user_agent'),
    platform: varchar('platform', { length: 120 }),
    isActive: boolean('is_active').default(true).notNull(),
    lastSuccessAt: datetime('last_success_at'),
    lastFailureAt: datetime('last_failure_at'),
    failureReason: text('failure_reason'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => ({
    endpointHashIdx: uniqueIndex('push_subscriptions_endpoint_hash_idx').on(table.endpointHash),
    userActiveIdx: index('push_subscriptions_user_active_idx').on(table.userId, table.isActive),
  })
);

export const leadAssignmentsHistory = mysqlTable(
  'lead_assignments_history',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    leadId: bigint('lead_id', { mode: 'number' })
      .references(() => leads.id)
      .notNull(),
    previousAssignedUserId: bigint('previous_assigned_user_id', {
      mode: 'number',
    }).references(() => users.id),
    newAssignedUserId: bigint('new_assigned_user_id', {
      mode: 'number',
    }).references(() => users.id),
    changedBy: bigint('changed_by', { mode: 'number' })
      .references(() => users.id)
      .notNull(),
    reason: varchar('reason', { length: 255 }),
    changedAt: timestamp('changed_at').defaultNow().notNull(),
  },
  table => ({
    leadChangedAtIdx: index('lead_assignments_history_lead_changed_at_idx').on(
      table.leadId,
      table.changedAt
    ),
    previousAssigneeIdx: index('lead_assignments_history_previous_assignee_idx').on(
      table.previousAssignedUserId
    ),
    newAssigneeIdx: index('lead_assignments_history_new_assignee_idx').on(table.newAssignedUserId),
  })
);

export const leadStageHistory = mysqlTable(
  'lead_stage_history',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    leadId: bigint('lead_id', { mode: 'number' })
      .references(() => leads.id, { onDelete: 'cascade' })
      .notNull(),
    adminId: bigint('admin_id', { mode: 'number' }).references(() => users.id, {
      onDelete: 'set null',
    }),
    eventType: leadStageHistoryEventTypeEnum.notNull(),
    previousPipelineStage: leadStageHistoryPreviousPipelineStageEnum,
    nextPipelineStage: leadStageHistoryNextPipelineStageEnum.notNull(),
    previousStatus: leadStageHistoryPreviousStatusEnum,
    nextStatus: leadStageHistoryNextStatusEnum.notNull(),
    changedByUserId: bigint('changed_by_user_id', { mode: 'number' }).references(() => users.id, {
      onDelete: 'set null',
    }),
    reason: varchar('reason', { length: 500 }),
    changedAt: timestamp('changed_at').defaultNow().notNull(),
  },
  table => ({
    leadChangedAtIdx: index('lead_stage_history_lead_changed_at_idx').on(
      table.leadId,
      table.changedAt
    ),
    adminChangedAtIdx: index('lead_stage_history_admin_changed_at_idx').on(
      table.adminId,
      table.changedAt
    ),
    adminStageChangedAtIdx: index('lead_stage_history_admin_stage_changed_at_idx').on(
      table.adminId,
      table.nextPipelineStage,
      table.changedAt
    ),
  })
);

export const notificationTemplates = mysqlTable(
  'notification_templates',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    templateKey: varchar('template_key', { length: 191 }).notNull(),
    title: varchar('title', { length: 255 }).notNull(),
    category: varchar('category', { length: 100 }).notNull(),
    eventKey: varchar('event_key', { length: 191 }),
    channel: mysqlEnum('channel', notificationChannels).notNull(),
    adminId: bigint('admin_id', { mode: 'number' }).references(() => users.id, {
      onDelete: 'cascade',
    }),
    productId: bigint('product_id', { mode: 'number' }).references(() => products.id, {
      onDelete: 'restrict',
    }),
    language: varchar('language', { length: 20 }).default(defaultLocale).notNull(),
    // WhatsApp Business Account that owns this template. Numbers under the same WABA
    // share templates; NULL means legacy/platform templates visible to every number.
    wabaId: varchar('waba_id', { length: 191 }),
    metaCategory: varchar('meta_category', { length: 40 }),
    providerTemplateName: varchar('provider_template_name', { length: 191 }),
    providerTemplateId: varchar('provider_template_id', { length: 191 }),
    metaTemplateStatus: varchar('meta_template_status', { length: 60 }),
    metaParameterFormat: mysqlEnum('meta_parameter_format', ['NAMED', 'POSITIONAL']),
    metaRejectionReason: text('meta_rejection_reason'),
    bodyTemplate: text('body_template').notNull(),
    bodyPreviewText: text('body_preview_text'),
    componentsJson: text('components_json'),
    variableDefinitions: text('variable_definitions'),
    samplePayload: text('sample_payload'),
    isActive: boolean('is_active').default(true).notNull(),
    isDraft: boolean('is_draft').default(true).notNull(),
    source: varchar('source', { length: 40 }).default('seed').notNull(),
    syncStatus: varchar('sync_status', { length: 40 }).default('LOCAL_ONLY').notNull(),
    lastSyncError: text('last_sync_error'),
    lastSyncedAt: datetime('last_synced_at'),
    lastPushedAt: datetime('last_pushed_at'),
    versionNumber: int('version_number').default(1).notNull(),
    parentTemplateId: bigint('parent_template_id', { mode: 'number' }),
    editableOnMeta: boolean('editable_on_meta').default(false).notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => ({
    templateKeyIdx: uniqueIndex('notification_templates_template_key_idx').on(
      table.adminId,
      table.templateKey,
      table.channel,
      table.language
    ),
    adminChannelActiveIdx: index('notification_templates_admin_channel_active_idx').on(
      table.adminId,
      table.channel,
      table.isActive
    ),
    channelActiveIdx: index('notification_templates_channel_active_idx').on(
      table.channel,
      table.isActive
    ),
    providerTemplateIdIdx: index('notification_templates_provider_template_id_idx').on(
      table.providerTemplateId
    ),
    providerTemplateNameIdx: index('notification_templates_provider_template_name_idx').on(
      table.providerTemplateName
    ),
    syncStatusIdx: index('notification_templates_sync_status_idx').on(table.syncStatus),
    metaStatusIdx: index('notification_templates_meta_status_idx').on(table.metaTemplateStatus),
    productIdx: index('notification_templates_product_id_idx').on(table.productId),
    parentTemplateIdx: index('notification_templates_parent_template_id_idx').on(
      table.parentTemplateId
    ),
    wabaIdx: index('notification_templates_waba_idx').on(table.adminId, table.wabaId),
  })
);

export const whatsappAccounts = mysqlTable(
  'whatsapp_accounts',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    adminId: bigint('admin_id', { mode: 'number' }).notNull(),
    wabaId: varchar('waba_id', { length: 191 }).notNull(),
    phoneNumberId: varchar('phone_number_id', { length: 191 }).notNull(),
    accessToken: text('access_token').notNull(),
    businessName: varchar('business_name', { length: 255 }),
    displayPhoneNumber: varchar('display_phone_number', { length: 50 }),
    metaUserId: varchar('meta_user_id', { length: 191 }),
    metaBusinessId: varchar('meta_business_id', { length: 191 }),
    // Meta catalog linked to this number's WABA and its shareable wa.me/c link, refreshed on link
    // and on each live catalog lookup so AI replies can share it without a Graph call.
    metaCatalogId: varchar('meta_catalog_id', { length: 191 }),
    metaCatalogUrl: varchar('meta_catalog_url', { length: 512 }),
    connectionMode: mysqlEnum('connection_mode', ['CLOUD_API', 'COEXISTENCE'])
      .default('CLOUD_API')
      .notNull(),
    contactSyncStatus: mysqlEnum('contact_sync_status', [
      'NOT_REQUESTED',
      'REQUESTED',
      'COMPLETED',
      'FAILED',
    ])
      .default('NOT_REQUESTED')
      .notNull(),
    historySyncStatus: mysqlEnum('history_sync_status', [
      'NOT_REQUESTED',
      'REQUESTED',
      'COMPLETED',
      'SKIPPED',
      'FAILED',
    ])
      .default('NOT_REQUESTED')
      .notNull(),
    coexistenceSyncStartedAt: datetime('coexistence_sync_started_at'),
    lastAccountEventAt: datetime('last_account_event_at'),
    contactSyncRequestId: varchar('contact_sync_request_id', { length: 191 }),
    historySyncRequestId: varchar('history_sync_request_id', { length: 191 }),
    historySyncProgress: int('history_sync_progress'),
    lastVerifiedAt: datetime('last_verified_at'),
    lastWebhookAt: datetime('last_webhook_at'),
    lastErrorCode: varchar('last_error_code', { length: 80 }),
    lastErrorMessage: text('last_error_message'),
    tokenCreatedAt: datetime('token_created_at'),
    tokenExpiresAt: datetime('token_expires_at'),
    lastSyncedAt: datetime('last_synced_at'),
    isActive: boolean('is_active').default(true).notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    adminActiveIdx: index('whatsapp_accounts_admin_active_idx').on(
      table.adminId,
      table.isActive,
      table.createdAt
    ),
    phoneNumberUniqueIdx: uniqueIndex('whatsapp_accounts_phone_number_id_idx').on(
      table.phoneNumberId
    ),
    wabaActiveIdx: index('whatsapp_accounts_waba_active_idx').on(table.wabaId, table.isActive),
    metaUserIdx: index('whatsapp_accounts_meta_user_idx').on(table.metaUserId),
    metaBusinessIdx: index('whatsapp_accounts_meta_business_idx').on(table.metaBusinessId),
    adminFk: foreignKey({
      name: 'whatsapp_accounts_admin_id_fk',
      columns: [table.adminId],
      foreignColumns: [users.id],
    }).onDelete('cascade'),
  })
);

export const whatsappSyncedContacts = mysqlTable(
  'whatsapp_synced_contacts',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    adminId: bigint('admin_id', { mode: 'number' }).notNull(),
    whatsappAccountId: bigint('whatsapp_account_id', { mode: 'number' }).notNull(),
    phoneNumber: varchar('phone_number', { length: 32 }).notNull(),
    fullName: varchar('full_name', { length: 255 }),
    firstName: varchar('first_name', { length: 120 }),
    isActive: boolean('is_active').default(true).notNull(),
    providerUpdatedAt: datetime('provider_updated_at'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    accountPhoneUniqueIdx: uniqueIndex('whatsapp_synced_contacts_account_phone_idx').on(
      table.whatsappAccountId,
      table.phoneNumber
    ),
    adminPhoneIdx: index('whatsapp_synced_contacts_admin_phone_idx').on(
      table.adminId,
      table.phoneNumber
    ),
    accountFk: foreignKey({
      name: 'whatsapp_synced_contacts_account_id_fk',
      columns: [table.whatsappAccountId],
      foreignColumns: [whatsappAccounts.id],
    }).onDelete('cascade'),
    adminFk: foreignKey({
      name: 'whatsapp_synced_contacts_admin_id_fk',
      columns: [table.adminId],
      foreignColumns: [users.id],
    }).onDelete('cascade'),
  })
);

// A workspace can connect more than one social asset. Provider tokens are encrypted before
// persistence by the social integration service.
export const socialAccounts = mysqlTable(
  'social_accounts',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    adminId: bigint('admin_id', { mode: 'number' }).notNull(),
    platform: socialPlatformEnum.notNull(),
    providerAccountId: varchar('provider_account_id', { length: 191 }).notNull(),
    providerPageId: varchar('provider_page_id', { length: 191 }),
    metaUserId: varchar('meta_user_id', { length: 191 }),
    assetName: varchar('asset_name', { length: 255 }),
    accessToken: text('access_token').notNull(),
    refreshToken: text('refresh_token'),
    tokenExpiresAt: datetime('token_expires_at'),
    isActive: boolean('is_active').default(true).notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    adminPlatformAssetUnique: uniqueIndex('social_accounts_admin_platform_asset_idx').on(
      table.adminId,
      table.platform,
      table.providerAccountId
    ),
    platformAssetIdx: index('social_accounts_platform_asset_idx').on(
      table.platform,
      table.providerAccountId,
      table.isActive
    ),
    metaUserIdx: index('social_accounts_meta_user_idx').on(table.metaUserId),
    adminFk: foreignKey({
      name: 'social_accounts_admin_id_fk',
      columns: [table.adminId],
      foreignColumns: [users.id],
    }).onDelete('cascade'),
  })
);

// Provider feeds remain live-only. This durable table records the local automation workflow,
// billable provider operations, and per-platform publishing results.
export const aiSocialPosts = mysqlTable(
  'ai_social_posts',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    adminId: bigint('admin_id', { mode: 'number' }).notNull(),
    productId: bigint('product_id', { mode: 'number' }).notNull(),
    createdBy: bigint('created_by', { mode: 'number' }),
    approvedBy: bigint('approved_by', { mode: 'number' }),
    scheduleWindow: varchar('schedule_window', { length: 64 }).notNull(),
    targetPlatforms: json('target_platforms')
      .$type<Array<'facebook' | 'instagram' | 'threads'>>()
      .notNull(),
    mediaType: aiSocialPostMediaTypeEnum.notNull(),
    generationStatus: aiSocialPostGenerationStatusEnum.default('scheduled').notNull(),
    approvalStatus: aiSocialPostApprovalStatusEnum.default('pending').notNull(),
    publishStatus: aiSocialPostPublishStatusEnum.default('not_requested').notNull(),
    caption: text('caption'),
    mediaStorageKey: varchar('media_storage_key', { length: 768 }),
    mediaUrl: varchar('media_url', { length: 2048 }),
    mediaMimeType: varchar('media_mime_type', { length: 100 }),
    productSnapshot: json('product_snapshot').$type<Record<string, unknown>>().notNull(),
    generationConfig: json('generation_config').$type<Record<string, unknown>>().notNull(),
    billingJson: json('billing_json').$type<Record<string, unknown>>().notNull(),
    publishResults: json('publish_results').$type<Array<Record<string, unknown>>>(),
    providerOperationId: varchar('provider_operation_id', { length: 255 }),
    errorMessage: text('error_message'),
    scheduledAt: datetime('scheduled_at').notNull(),
    nextAttemptAt: datetime('next_attempt_at').notNull(),
    lockedUntil: datetime('locked_until'),
    processingToken: varchar('processing_token', { length: 64 }),
    attemptCount: int('attempt_count').default(0).notNull(),
    approvedAt: datetime('approved_at'),
    publishedAt: datetime('published_at'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    adminProductWindowUnique: uniqueIndex('ai_social_posts_admin_product_window_idx').on(
      table.adminId,
      table.productId,
      table.scheduleWindow
    ),
    dueIdx: index('ai_social_posts_due_idx').on(
      table.generationStatus,
      table.publishStatus,
      table.nextAttemptAt,
      table.id
    ),
    adminCreatedIdx: index('ai_social_posts_admin_created_idx').on(table.adminId, table.createdAt),
    productFk: foreignKey({
      name: 'ai_social_posts_product_id_fk',
      columns: [table.productId],
      foreignColumns: [products.id],
    }).onDelete('restrict'),
    adminFk: foreignKey({
      name: 'ai_social_posts_admin_id_fk',
      columns: [table.adminId],
      foreignColumns: [users.id],
    }).onDelete('cascade'),
    createdByFk: foreignKey({
      name: 'ai_social_posts_created_by_fk',
      columns: [table.createdBy],
      foreignColumns: [users.id],
    }).onDelete('set null'),
    approvedByFk: foreignKey({
      name: 'ai_social_posts_approved_by_fk',
      columns: [table.approvedBy],
      foreignColumns: [users.id],
    }).onDelete('set null'),
  })
);

// One row per paid (or cache-served) AI media operation. It is the idempotency, budget, cost
// observability, and reusable-asset ledger for `ai_social_posts`: a completed row with an
// `input_hash` and output columns is a reusable media asset; `lock_key` holds the input hash only
// while the operation is in flight so two workers cannot generate the same asset concurrently.
export const aiGenerationOperations = mysqlTable(
  'ai_generation_operations',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    jobId: bigint('job_id', { mode: 'number' }).notNull(),
    adminId: bigint('admin_id', { mode: 'number' }).notNull(),
    productId: bigint('product_id', { mode: 'number' }).notNull(),
    operationType: aiGenerationOperationTypeEnum.notNull(),
    provider: varchar('provider', { length: 64 }).notNull(),
    model: varchar('model', { length: 191 }).notNull(),
    strategy: varchar('strategy', { length: 64 }),
    status: aiGenerationOperationStatusEnum.default('pending').notNull(),
    attempt: int('attempt').default(1).notNull(),
    providerOperationId: varchar('provider_operation_id', { length: 255 }),
    billingComponent: varchar('billing_component', { length: 64 }),
    idempotencyKey: varchar('idempotency_key', { length: 191 }),
    estimatedCredits: decimal('estimated_credits', { precision: 12, scale: 2 })
      .default('0.00')
      .notNull(),
    actualCredits: decimal('actual_credits', { precision: 12, scale: 2 }).default('0.00').notNull(),
    cacheHit: boolean('cache_hit').default(false).notNull(),
    reusedFromOperationId: bigint('reused_from_operation_id', { mode: 'number' }),
    inputHash: varchar('input_hash', { length: 64 }),
    lockKey: varchar('lock_key', { length: 191 }),
    requestedDurationSeconds: int('requested_duration_seconds'),
    outputStorageKey: varchar('output_storage_key', { length: 768 }),
    outputUrl: varchar('output_url', { length: 2048 }),
    outputMimeType: varchar('output_mime_type', { length: 100 }),
    outputSha256: varchar('output_sha256', { length: 64 }),
    outputBytes: int('output_bytes'),
    outputDurationSeconds: decimal('output_duration_seconds', { precision: 8, scale: 3 }),
    outputWidth: int('output_width'),
    outputHeight: int('output_height'),
    metadataJson: json('metadata_json').$type<Record<string, unknown>>(),
    errorCode: varchar('error_code', { length: 64 }),
    errorMessage: text('error_message'),
    startedAt: datetime('started_at'),
    completedAt: datetime('completed_at'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    providerOperationIdx: uniqueIndex('ai_generation_operations_provider_operation_idx').on(
      table.providerOperationId
    ),
    lockKeyIdx: uniqueIndex('ai_generation_operations_lock_key_idx').on(table.lockKey),
    idempotencyIdx: uniqueIndex('ai_generation_operations_idempotency_idx').on(
      table.idempotencyKey
    ),
    jobIdx: index('ai_generation_operations_job_idx').on(
      table.jobId,
      table.operationType,
      table.status
    ),
    inputHashIdx: index('ai_generation_operations_input_hash_idx').on(
      table.adminId,
      table.inputHash,
      table.status
    ),
    statusIdx: index('ai_generation_operations_status_idx').on(table.status, table.operationType),
    jobFk: foreignKey({
      name: 'ai_generation_operations_job_id_fk',
      columns: [table.jobId],
      foreignColumns: [aiSocialPosts.id],
    }).onDelete('cascade'),
    adminFk: foreignKey({
      name: 'ai_generation_operations_admin_id_fk',
      columns: [table.adminId],
      foreignColumns: [users.id],
    }).onDelete('cascade'),
  })
);

export const gmailAccounts = mysqlTable(
  'gmail_accounts',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    adminId: bigint('admin_id', { mode: 'number' }).notNull(),
    emailAddress: varchar('email_address', { length: 320 }).notNull(),
    refreshToken: text('refresh_token').notNull(),
    accessToken: text('access_token'),
    tokenExpiresAt: datetime('token_expires_at'),
    historyId: varchar('history_id', { length: 64 }),
    watchExpiresAt: datetime('watch_expires_at'),
    lastSyncedAt: datetime('last_synced_at'),
    lastError: text('last_error'),
    isActive: boolean('is_active').default(true).notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    adminActiveIdx: index('gmail_accounts_admin_active_idx').on(
      table.adminId,
      table.isActive,
      table.createdAt
    ),
    emailUniqueIdx: uniqueIndex('gmail_accounts_email_idx').on(table.emailAddress),
    adminFk: foreignKey({
      name: 'gmail_accounts_admin_id_fk',
      columns: [table.adminId],
      foreignColumns: [users.id],
    }).onDelete('cascade'),
  })
);

export const gmailProcessedMessages = mysqlTable(
  'gmail_processed_messages',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    gmailAccountId: bigint('gmail_account_id', { mode: 'number' }).notNull(),
    providerMessageId: varchar('provider_message_id', { length: 191 }).notNull(),
    providerThreadId: varchar('provider_thread_id', { length: 191 }),
    source: varchar('source', { length: 40 }),
    status: varchar('status', { length: 24 }).notNull(),
    leadId: bigint('lead_id', { mode: 'number' }).references(() => leads.id, {
      onDelete: 'set null',
    }),
    failureReason: text('failure_reason'),
    receivedAt: datetime('received_at'),
    processedAt: timestamp('processed_at').defaultNow().notNull(),
  },
  table => ({
    messageUniqueIdx: uniqueIndex('gmail_processed_messages_account_message_idx').on(
      table.gmailAccountId,
      table.providerMessageId
    ),
    accountProcessedIdx: index('gmail_processed_messages_account_processed_idx').on(
      table.gmailAccountId,
      table.processedAt
    ),
    accountFk: foreignKey({
      name: 'gmail_processed_messages_account_id_fk',
      columns: [table.gmailAccountId],
      foreignColumns: [gmailAccounts.id],
    }).onDelete('cascade'),
  })
);

export const whatsappBroadcastSubscriptions = mysqlTable(
  'whatsapp_broadcast_subscriptions',
  {
    wabaId: varchar('waba_id', { length: 191 }).notNull(),
    phoneNumber: varchar('phone_number', { length: 50 }).notNull(),
    unsubscribed: boolean('unsubscribed').default(false).notNull(),
    lastMessageAt: datetime('last_message_at', { fsp: 3 }).notNull(),
    lastMessageId: bigint('last_message_id', { mode: 'number' }).notNull(),
  },
  table => ({
    pk: primaryKey({ columns: [table.wabaId, table.phoneNumber] }),
  })
);

export const broadcastCampaigns = mysqlTable(
  'broadcast_campaigns',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    adminId: bigint('admin_id', { mode: 'number' }).notNull(),
    createdByUserId: bigint('created_by_user_id', { mode: 'number' }).notNull(),
    name: varchar('name', { length: 255 }).notNull(),
    templateId: bigint('template_id', { mode: 'number' }),
    whatsappAccountId: bigint('whatsapp_account_id', { mode: 'number' }),
    filtersJson: json('filters_json'),
    totalRecipients: int('total_recipients').default(0).notNull(),
    dispatchKey: varchar('dispatch_key', { length: 64 }).notNull(),
    status: broadcastCampaignStatusEnum.default('draft').notNull(),
    scheduledAt: datetime('scheduled_at'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    adminStatusIdx: index('broadcast_campaigns_admin_status_idx').on(
      table.adminId,
      table.status,
      table.createdAt
    ),
    creatorIdx: index('broadcast_campaigns_creator_idx').on(table.createdByUserId, table.createdAt),
    templateIdx: index('broadcast_campaigns_template_idx').on(table.templateId),
    whatsappAccountIdx: index('broadcast_campaigns_whatsapp_account_idx').on(
      table.whatsappAccountId
    ),
    dispatchKeyIdx: uniqueIndex('broadcast_campaigns_dispatch_key_idx').on(table.dispatchKey),
    adminFk: foreignKey({
      name: 'broadcast_campaigns_admin_id_fk',
      columns: [table.adminId],
      foreignColumns: [users.id],
    }).onDelete('cascade'),
    creatorFk: foreignKey({
      name: 'broadcast_campaigns_created_by_user_id_fk',
      columns: [table.createdByUserId],
      foreignColumns: [users.id],
    }).onDelete('cascade'),
    templateFk: foreignKey({
      name: 'broadcast_campaigns_template_id_fk',
      columns: [table.templateId],
      foreignColumns: [notificationTemplates.id],
    }).onDelete('set null'),
    whatsappAccountFk: foreignKey({
      name: 'broadcast_campaigns_whatsapp_account_id_fk',
      columns: [table.whatsappAccountId],
      foreignColumns: [whatsappAccounts.id],
    }).onDelete('set null'),
  })
);

export const broadcastRecipients = mysqlTable(
  'broadcast_recipients',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    campaignId: bigint('campaign_id', { mode: 'number' }).notNull(),
    leadId: bigint('lead_id', { mode: 'number' }),
    recipientName: varchar('recipient_name', { length: 255 }),
    recipientEmail: varchar('recipient_email', { length: 255 }),
    phoneNumber: varchar('phone_number', { length: 50 }).notNull(),
    source: varchar('source', { length: 50 }).default('lead').notNull(),
    status: broadcastRecipientStatusEnum.default('pending').notNull(),
    sentAt: datetime('sent_at'),
    errorMessage: text('error_message'),
    providerMessageId: varchar('provider_message_id', { length: 191 }),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    campaignStatusIdx: index('broadcast_recipients_campaign_status_idx').on(
      table.campaignId,
      table.status,
      table.id
    ),
    leadIdx: index('broadcast_recipients_lead_idx').on(table.leadId),
    campaignLeadUniqueIdx: uniqueIndex('broadcast_recipients_campaign_lead_idx').on(
      table.campaignId,
      table.leadId
    ),
    campaignFk: foreignKey({
      name: 'broadcast_recipients_campaign_id_fk',
      columns: [table.campaignId],
      foreignColumns: [broadcastCampaigns.id],
    }).onDelete('cascade'),
    leadFk: foreignKey({
      name: 'broadcast_recipients_lead_id_fk',
      columns: [table.leadId],
      foreignColumns: [leads.id],
    }).onDelete('cascade'),
  })
);

export const broadcastCampaignVariables = mysqlTable(
  'broadcast_campaign_variables',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    campaignId: bigint('campaign_id', { mode: 'number' }).notNull(),
    variableName: varchar('variable_name', { length: 120 }).notNull(),
    variableValue: text('variable_value').notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    campaignIdx: index('broadcast_campaign_variables_campaign_idx').on(table.campaignId),
    campaignNameUniqueIdx: uniqueIndex('broadcast_campaign_variables_campaign_name_idx').on(
      table.campaignId,
      table.variableName
    ),
    campaignFk: foreignKey({
      name: 'broadcast_campaign_variables_campaign_id_fk',
      columns: [table.campaignId],
      foreignColumns: [broadcastCampaigns.id],
    }).onDelete('cascade'),
  })
);

export const broadcastRecipientGroups = mysqlTable(
  'broadcast_recipient_groups',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    adminId: bigint('admin_id', { mode: 'number' }).notNull(),
    createdByUserId: bigint('created_by_user_id', { mode: 'number' }).notNull(),
    name: varchar('name', { length: 255 }).notNull(),
    description: text('description'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    adminIdx: index('broadcast_recipient_groups_admin_idx').on(table.adminId, table.createdAt),
    creatorIdx: index('broadcast_recipient_groups_creator_idx').on(
      table.createdByUserId,
      table.createdAt
    ),
    adminFk: foreignKey({
      name: 'broadcast_recipient_groups_admin_id_fk',
      columns: [table.adminId],
      foreignColumns: [users.id],
    }).onDelete('cascade'),
    creatorFk: foreignKey({
      name: 'broadcast_recipient_groups_created_by_user_id_fk',
      columns: [table.createdByUserId],
      foreignColumns: [users.id],
    }).onDelete('cascade'),
  })
);

export const broadcastRecipientGroupMembers = mysqlTable(
  'broadcast_recipient_group_members',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    groupId: bigint('group_id', { mode: 'number' }).notNull(),
    leadId: bigint('lead_id', { mode: 'number' }),
    name: varchar('name', { length: 255 }),
    phoneNumber: varchar('phone_number', { length: 50 }).notNull(),
    email: varchar('email', { length: 255 }),
    notes: text('notes'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    groupIdx: index('broadcast_recipient_group_members_group_idx').on(table.groupId, table.id),
    leadIdx: index('broadcast_recipient_group_members_lead_idx').on(table.leadId),
    groupPhoneUniqueIdx: uniqueIndex('broadcast_recipient_group_members_group_phone_idx').on(
      table.groupId,
      table.phoneNumber
    ),
    groupFk: foreignKey({
      name: 'broadcast_recipient_group_members_group_id_fk',
      columns: [table.groupId],
      foreignColumns: [broadcastRecipientGroups.id],
    }).onDelete('cascade'),
    leadFk: foreignKey({
      name: 'broadcast_recipient_group_members_lead_id_fk',
      columns: [table.leadId],
      foreignColumns: [leads.id],
    }).onDelete('set null'),
  })
);

export const conversations = mysqlTable(
  'conversations',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    leadId: bigint('lead_id', { mode: 'number' }),
    adminId: bigint('admin_id', { mode: 'number' }).notNull(),
    whatsappAccountId: bigint('whatsapp_account_id', { mode: 'number' }),
    channel: conversationChannelEnum.default('whatsapp').notNull(),
    socialAccountId: bigint('social_account_id', { mode: 'number' }),
    externalParticipantId: varchar('external_participant_id', { length: 191 }),
    interactionType: socialInteractionTypeEnum,
    sourceContentId: varchar('source_content_id', { length: 191 }),
    sourceContext: json('source_context'),
    socialIdentityKey: varchar('social_identity_key', { length: 64 }),
    socialAssistantState: json('social_assistant_state'),
    contactName: varchar('contact_name', { length: 255 }),
    phoneNumber: varchar('phone_number', { length: 50 }),
    createdByUserId: bigint('created_by_user_id', { mode: 'number' }).notNull(),
    lastMessage: text('last_message'),
    lastMessageAt: datetime('last_message_at'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    leadAdminAccountUniqueIdx: uniqueIndex('conversations_lead_admin_account_idx').on(
      table.leadId,
      table.adminId,
      table.whatsappAccountId
    ),
    adminPhoneAccountUniqueIdx: uniqueIndex('conversations_admin_phone_account_idx').on(
      table.adminId,
      table.phoneNumber,
      table.whatsappAccountId
    ),
    createdByIdx: index('conversations_created_by_idx').on(table.createdByUserId),
    whatsappAccountIdx: index('conversations_whatsapp_account_idx').on(table.whatsappAccountId),
    socialParticipantIdx: index('conversations_social_participant_idx').on(
      table.adminId,
      table.channel,
      table.socialAccountId,
      table.externalParticipantId
    ),
    socialIdentityUniqueIdx: uniqueIndex('conversations_social_identity_unique').on(
      table.socialIdentityKey
    ),
    adminLastMessageIdx: index('conversations_admin_last_message_idx').on(
      table.adminId,
      table.lastMessageAt,
      table.id
    ),
    leadFk: foreignKey({
      name: 'conversations_lead_id_fk',
      columns: [table.leadId],
      foreignColumns: [leads.id],
    }).onDelete('cascade'),
    adminFk: foreignKey({
      name: 'conversations_admin_id_fk',
      columns: [table.adminId],
      foreignColumns: [users.id],
    }).onDelete('cascade'),
    createdByFk: foreignKey({
      name: 'conversations_created_by_user_id_fk',
      columns: [table.createdByUserId],
      foreignColumns: [users.id],
    }).onDelete('restrict'),
    whatsappAccountFk: foreignKey({
      name: 'conversations_whatsapp_account_id_fk',
      columns: [table.whatsappAccountId],
      foreignColumns: [whatsappAccounts.id],
    }).onDelete('set null'),
    socialAccountFk: foreignKey({
      name: 'conversations_social_account_id_fk',
      columns: [table.socialAccountId],
      foreignColumns: [socialAccounts.id],
    }).onDelete('set null'),
  })
);

export const conversationAutomationStates = mysqlTable(
  'conversation_automation_states',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    conversationId: bigint('conversation_id', { mode: 'number' }).notNull(),
    adminId: bigint('admin_id', { mode: 'number' }).notNull(),
    isHumanTyping: boolean('is_human_typing').default(false).notNull(),
    humanTypingExpiresAt: datetime('human_typing_expires_at'),
    humanTakeoverUntil: datetime('human_takeover_until'),
    takeoverVersion: int('takeover_version').default(0).notNull(),
    aiThinkingUntil: datetime('ai_thinking_until'),
    lastHumanOutboundAt: datetime('last_human_outbound_at'),
    lastAiOutboundAt: datetime('last_ai_outbound_at'),
    updatedByUserId: bigint('updated_by_user_id', { mode: 'number' }),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    conversationUniqueIdx: uniqueIndex('conversation_automation_states_conversation_idx').on(
      table.conversationId
    ),
    adminIdx: index('conversation_automation_states_admin_idx').on(table.adminId, table.id),
    typingExpiresIdx: index('conversation_automation_states_typing_expires_idx').on(
      table.humanTypingExpiresAt
    ),
    takeoverUntilIdx: index('conversation_automation_states_takeover_until_idx').on(
      table.humanTakeoverUntil
    ),
    aiThinkingUntilIdx: index('conversation_automation_states_ai_thinking_until_idx').on(
      table.aiThinkingUntil
    ),
    conversationFk: foreignKey({
      name: 'conversation_automation_states_conversation_id_fk',
      columns: [table.conversationId],
      foreignColumns: [conversations.id],
    }).onDelete('cascade'),
    adminFk: foreignKey({
      name: 'conversation_automation_states_admin_id_fk',
      columns: [table.adminId],
      foreignColumns: [users.id],
    }).onDelete('cascade'),
    updatedByFk: foreignKey({
      name: 'conversation_automation_states_updated_by_user_id_fk',
      columns: [table.updatedByUserId],
      foreignColumns: [users.id],
    }).onDelete('set null'),
  })
);

export const messages = mysqlTable(
  'messages',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    conversationId: bigint('conversation_id', { mode: 'number' }).notNull(),
    direction: messageDirectionEnum.notNull(),
    messageType: conversationMessageTypeEnum.default('text').notNull(),
    content: longtext('content').notNull(),
    providerMessageId: varchar('provider_message_id', { length: 191 }),
    providerMediaId: varchar('provider_media_id', { length: 191 }),
    mediaMimeType: varchar('media_mime_type', { length: 191 }),
    mediaFileName: varchar('media_file_name', { length: 255 }),
    mediaStorageKey: varchar('media_storage_key', { length: 255 }),
    mediaSizeBytes: int('media_size_bytes'),
    socialContext: json('social_context'),
    providerPayload: json('provider_payload'),
    deliveryStatus: messageDeliveryStatusEnum,
    deliveredAt: datetime('delivered_at'),
    readAt: datetime('read_at'),
    deliveryError: text('delivery_error'),
    createdByUserId: bigint('created_by_user_id', { mode: 'number' }),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => ({
    conversationCreatedIdx: index('messages_conversation_created_idx').on(
      table.conversationId,
      table.createdAt,
      table.id
    ),
    providerMessageIdIdx: uniqueIndex('messages_provider_message_id_idx').on(
      table.providerMessageId
    ),
    providerMediaIdIdx: index('messages_provider_media_id_idx').on(table.providerMediaId),
    conversationFk: foreignKey({
      name: 'messages_conversation_id_fk',
      columns: [table.conversationId],
      foreignColumns: [conversations.id],
    }).onDelete('cascade'),
    creatorFk: foreignKey({
      name: 'messages_created_by_user_id_fk',
      columns: [table.createdByUserId],
      foreignColumns: [users.id],
    }).onDelete('set null'),
  })
);

// Per-job outbound operation ledger keyed by a deterministic idempotency key such as
// `wa-ai:{jobId}:text`, `wa-ai:{jobId}:image:{mediaId}`, or `wa-ai:{jobId}:video:{mediaId}`.
// `pending` and `ambiguous` operations are
// never resent automatically because the provider may already have accepted them.
export type ConversationAiDeliveryOperation = {
  kind: 'text' | 'catalog' | 'image' | 'video';
  status: 'pending' | 'accepted' | 'failed' | 'ambiguous';
  attempt: number;
  tries: number;
  providerMessageId: string | null;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
};
export type ConversationAiDeliveryLedger = Record<string, ConversationAiDeliveryOperation>;

export const conversationAiAutomationJobs = mysqlTable(
  'conversation_ai_automation_jobs',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    conversationId: bigint('conversation_id', { mode: 'number' }).notNull(),
    adminId: bigint('admin_id', { mode: 'number' }).notNull(),
    inboundMessageId: bigint('inbound_message_id', { mode: 'number' }).notNull(),
    status: conversationAiAutomationJobStatusEnum.default('pending').notNull(),
    scheduledAt: datetime('scheduled_at').notNull(),
    processingStartedAt: datetime('processing_started_at'),
    completedAt: datetime('completed_at'),
    attemptCount: int('attempt_count').default(0).notNull(),
    takeoverVersion: int('takeover_version').default(0).notNull(),
    reservationAmount: decimal('reservation_amount', { precision: 12, scale: 2 }),
    settledAmount: decimal('settled_amount', { precision: 12, scale: 2 }),
    model: varchar('model', { length: 120 }),
    replyText: text('reply_text'),
    analysisJson: json('analysis_json'),
    deliveryJson: json('delivery_json').$type<ConversationAiDeliveryLedger>(),
    providerMessageId: varchar('provider_message_id', { length: 191 }),
    failureReason: text('failure_reason'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    inboundMessageUniqueIdx: uniqueIndex('conversation_ai_automation_jobs_inbound_message_idx').on(
      table.inboundMessageId
    ),
    dueIdx: index('conversation_ai_automation_jobs_due_idx').on(
      table.status,
      table.scheduledAt,
      table.id
    ),
    conversationStatusIdx: index('conversation_ai_automation_jobs_conversation_status_idx').on(
      table.conversationId,
      table.status,
      table.id
    ),
    conversationFk: foreignKey({
      name: 'conversation_ai_automation_jobs_conversation_id_fk',
      columns: [table.conversationId],
      foreignColumns: [conversations.id],
    }).onDelete('cascade'),
    adminFk: foreignKey({
      name: 'conversation_ai_automation_jobs_admin_id_fk',
      columns: [table.adminId],
      foreignColumns: [users.id],
    }).onDelete('cascade'),
    inboundMessageFk: foreignKey({
      name: 'conversation_ai_automation_jobs_inbound_message_id_fk',
      columns: [table.inboundMessageId],
      foreignColumns: [messages.id],
    }).onDelete('cascade'),
  })
);

export const conversationReadStates = mysqlTable(
  'conversation_read_states',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    conversationId: bigint('conversation_id', { mode: 'number' }).notNull(),
    userId: bigint('user_id', { mode: 'number' }).notNull(),
    lastReadAt: datetime('last_read_at'),
    lastReadMessageId: bigint('last_read_message_id', { mode: 'number' }),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    conversationUserUniqueIdx: uniqueIndex('conversation_read_states_conversation_user_idx').on(
      table.conversationId,
      table.userId
    ),
    userLastReadIdx: index('conversation_read_states_user_last_read_idx').on(
      table.userId,
      table.lastReadAt
    ),
    conversationIdx: index('conversation_read_states_conversation_idx').on(table.conversationId),
    lastReadMessageIdx: index('conversation_read_states_last_read_message_idx').on(
      table.lastReadMessageId
    ),
    conversationFk: foreignKey({
      name: 'conversation_read_states_conversation_id_fk',
      columns: [table.conversationId],
      foreignColumns: [conversations.id],
    }).onDelete('cascade'),
    userFk: foreignKey({
      name: 'conversation_read_states_user_id_fk',
      columns: [table.userId],
      foreignColumns: [users.id],
    }).onDelete('cascade'),
    lastReadMessageFk: foreignKey({
      name: 'conversation_read_states_last_read_message_id_fk',
      columns: [table.lastReadMessageId],
      foreignColumns: [messages.id],
    }).onDelete('set null'),
  })
);

export const notificationQueue = mysqlTable(
  'notification_queue',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    eventKey: varchar('event_key', { length: 191 }).notNull(),
    templateKey: varchar('template_key', { length: 191 }).notNull(),
    channel: mysqlEnum('channel', notificationChannels).notNull(),
    status: mysqlEnum('status', notificationJobStatuses).default('pending').notNull(),
    recipientType: varchar('recipient_type', { length: 50 }).default('user').notNull(),
    recipientId: bigint('recipient_id', { mode: 'number' }),
    adminId: bigint('admin_id', { mode: 'number' }),
    phoneNumberSnapshot: varchar('phone_number_snapshot', { length: 50 }),
    payload: text('payload'),
    sourceEntityType: varchar('source_entity_type', { length: 100 }),
    sourceEntityId: bigint('source_entity_id', { mode: 'number' }),
    source: varchar('source', { length: 100 }),
    dedupeKey: varchar('dedupe_key', { length: 191 }),
    priority: int('priority').default(100).notNull(),
    scheduledAt: datetime('scheduled_at').notNull(),
    processingStartedAt: datetime('processing_started_at'),
    processingToken: varchar('processing_token', { length: 64 }),
    sentAt: datetime('sent_at'),
    retryCount: int('retry_count').default(0).notNull(),
    maxRetries: int('max_retries').default(3).notNull(),
    providerMessageId: varchar('provider_message_id', { length: 191 }),
    providerResponse: text('provider_response'),
    failureReason: text('failure_reason'),
    createdBy: bigint('created_by', { mode: 'number' }),
    metadata: text('metadata'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => ({
    dedupeKeyIdx: uniqueIndex('notification_queue_dedupe_key_idx').on(table.dedupeKey),
    statusScheduleIdx: index('notification_queue_status_schedule_idx').on(
      table.status,
      table.scheduledAt,
      table.priority
    ),
    recipientIdx: index('notification_queue_recipient_idx').on(table.recipientId, table.status),
    sourceIdx: index('notification_queue_source_idx').on(
      table.sourceEntityType,
      table.sourceEntityId,
      table.eventKey
    ),
    processingIdx: index('notification_queue_processing_idx').on(
      table.processingToken,
      table.status
    ),
    adminFk: foreignKey({
      name: 'notification_queue_admin_id_fk',
      columns: [table.adminId],
      foreignColumns: [users.id],
    }).onDelete('set null'),
    createdByFk: foreignKey({
      name: 'notification_queue_created_by_fk',
      columns: [table.createdBy],
      foreignColumns: [users.id],
    }).onDelete('set null'),
  })
);

export const notificationDeliveryAttempts = mysqlTable(
  'notification_delivery_attempts',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    notificationId: bigint('notification_id', { mode: 'number' }).notNull(),
    attemptNumber: int('attempt_number').notNull(),
    status: varchar('status', { length: 50 }).notNull(),
    requestPayload: text('request_payload'),
    responsePayload: text('response_payload'),
    providerMessageId: varchar('provider_message_id', { length: 191 }),
    failureReason: text('failure_reason'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => ({
    notificationAttemptIdx: index('notification_delivery_attempts_notification_idx').on(
      table.notificationId,
      table.attemptNumber
    ),
    notificationFk: foreignKey({
      name: 'notification_delivery_attempts_notification_fk',
      columns: [table.notificationId],
      foreignColumns: [notificationQueue.id],
    }).onDelete('cascade'),
  })
);

export const passwordResetRequests = mysqlTable(
  'password_reset_requests',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    userId: bigint('user_id', { mode: 'number' }).notNull(),
    phoneNumberSnapshot: varchar('phone_number_snapshot', { length: 50 }),
    status: mysqlEnum('status', passwordResetRequestStatuses).default('pending').notNull(),
    otpSalt: varchar('otp_salt', { length: 191 }).notNull(),
    otpHash: varchar('otp_hash', { length: 191 }).notNull(),
    otpExpiresAt: datetime('otp_expires_at').notNull(),
    resetTokenSalt: varchar('reset_token_salt', { length: 191 }),
    resetTokenHash: varchar('reset_token_hash', { length: 191 }),
    resetTokenExpiresAt: datetime('reset_token_expires_at'),
    attemptCount: int('attempt_count').default(0).notNull(),
    lastNotificationId: bigint('last_notification_id', { mode: 'number' }),
    verifiedAt: datetime('verified_at'),
    consumedAt: datetime('consumed_at'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => ({
    userStatusIdx: index('password_reset_requests_user_status_idx').on(
      table.userId,
      table.status,
      table.createdAt
    ),
    otpExpiryIdx: index('password_reset_requests_otp_expiry_idx').on(
      table.status,
      table.otpExpiresAt
    ),
    tokenHashIdx: index('password_reset_requests_token_hash_idx').on(table.resetTokenHash),
    notificationIdx: index('password_reset_requests_notification_idx').on(table.lastNotificationId),
    userFk: foreignKey({
      name: 'password_reset_requests_user_fk',
      columns: [table.userId],
      foreignColumns: [users.id],
    }).onDelete('cascade'),
    notificationFk: foreignKey({
      name: 'password_reset_requests_notification_fk',
      columns: [table.lastNotificationId],
      foreignColumns: [notificationQueue.id],
    }).onDelete('set null'),
  })
);

export const reportSavedFilters = mysqlTable(
  'report_saved_filters',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    userId: bigint('user_id', { mode: 'number' })
      .references(() => users.id)
      .notNull(),
    scope: mysqlEnum('report_scope', reportScopes).notNull(),
    name: varchar('name', { length: 191 }).notNull(),
    filtersJson: text('filters_json').notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => ({
    userScopeIdx: index('report_saved_filters_user_scope_idx').on(table.userId, table.scope),
    userNameIdx: uniqueIndex('report_saved_filters_user_name_idx').on(table.userId, table.name),
  })
);

export const leadAnalyticsSavedViews = mysqlTable(
  'lead_analytics_saved_views',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    userId: bigint('user_id', { mode: 'number' })
      .references(() => users.id, { onDelete: 'cascade' })
      .notNull(),
    name: varchar('name', { length: 191 }).notNull(),
    filtersJson: text('filters_json').notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  table => ({
    userUpdatedAtIdx: index('lead_analytics_saved_views_user_updated_at_idx').on(
      table.userId,
      table.updatedAt
    ),
    userNameIdx: uniqueIndex('lead_analytics_saved_views_user_name_idx').on(
      table.userId,
      table.name
    ),
  })
);

export const settingValues = mysqlTable(
  'setting_values',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    storageKey: varchar('storage_key', { length: 255 }).notNull(),
    companyId: bigint('company_id', { mode: 'number' }),
    scope: settingsValueScopeEnum.notNull(),
    settingKey: varchar('setting_key', { length: 191 }).notNull(),
    value: longtext('value').notNull(),
    updatedBy: bigint('updated_by', { mode: 'number' }),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => ({
    storageKeyIdx: uniqueIndex('setting_values_storage_key_idx').on(table.storageKey),
    scopeLookupIdx: index('setting_values_scope_lookup_idx').on(
      table.scope,
      table.companyId,
      table.settingKey
    ),
    companyIdx: index('setting_values_company_idx').on(table.companyId),
    companyFk: foreignKey({
      name: 'setting_values_company_fk',
      columns: [table.companyId],
      foreignColumns: [users.id],
    }).onDelete('cascade'),
    updatedByFk: foreignKey({
      name: 'setting_values_updated_by_fk',
      columns: [table.updatedBy],
      foreignColumns: [users.id],
    }).onDelete('set null'),
  })
);

export const settingsAuditLogs = mysqlTable(
  'settings_audit_logs',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    companyId: bigint('company_id', { mode: 'number' }),
    scope: settingsValueScopeEnum.notNull(),
    settingKey: varchar('setting_key', { length: 191 }).notNull(),
    previousValue: longtext('previous_value'),
    newValue: longtext('new_value'),
    changedBy: bigint('changed_by', { mode: 'number' }),
    changeReason: varchar('change_reason', { length: 255 }),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => ({
    companyKeyIdx: index('settings_audit_logs_company_key_idx').on(
      table.companyId,
      table.settingKey,
      table.createdAt
    ),
    scopeIdx: index('settings_audit_logs_scope_idx').on(table.scope, table.createdAt),
    companyFk: foreignKey({
      name: 'settings_audit_logs_company_fk',
      columns: [table.companyId],
      foreignColumns: [users.id],
    }).onDelete('set null'),
    changedByFk: foreignKey({
      name: 'settings_audit_logs_changed_by_fk',
      columns: [table.changedBy],
      foreignColumns: [users.id],
    }).onDelete('set null'),
  })
);

export const subscriptions = mysqlTable('subscriptions', {
  id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
  userId: bigint('user_id', { mode: 'number' })
    .references(() => users.id)
    .notNull(),
  razorpayOrderId: text('razorpay_order_id').notNull(),
  razorpayPaymentId: text('razorpay_payment_id'),
  razorpaySignature: text('razorpay_signature'),
  amount: int('amount').notNull(), // Amount paid in whole INR
  listPrice: int('list_price'), // Original GST-inclusive price in whole INR
  discountAmount: int('discount_amount'), // Applied discount in whole INR
  currency: varchar('currency', { length: 10 }).default('INR').notNull(),
  status: subscriptionRecordStatusEnum.default('pending').notNull(),
  startDate: datetime('start_date'),
  expiryDate: datetime('expiry_date'),
  couponCode: varchar('coupon_code', { length: 50 }),
  bonusAmount: int('bonus_amount'),
  invoiceSnapshotJson: json('invoice_snapshot_json').$type<Record<string, unknown>>(),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

export const couponDiscountTypeEnum = mysqlEnum('discount_type', ['percentage', 'fixed']);

export const coupons = mysqlTable(
  'coupons',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    code: varchar('code', { length: 50 }).notNull(),
    discountType: mysqlEnum('discount_type', ['percentage', 'fixed'])
      .default('percentage')
      .notNull(),
    discountValue: decimal('discount_value', { precision: 12, scale: 2 }).notNull(),
    maxUses: int('max_uses'),
    usedCount: int('used_count').default(0).notNull(),
    minOrderAmount: int('min_order_amount').default(0).notNull(),
    expiresAt: datetime('expires_at'),
    isActive: boolean('is_active').default(true).notNull(),
    createdBy: bigint('created_by', { mode: 'number' }).references(() => users.id),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    codeIdx: uniqueIndex('coupons_code_idx').on(table.code),
  })
);

export const insertCouponSchema = createInsertSchema(coupons).omit({
  id: true,
  usedCount: true,
  createdAt: true,
  updatedAt: true,
});

export type Coupon = typeof coupons.$inferSelect;
export type InsertCoupon = z.infer<typeof insertCouponSchema>;

export const servicePricing = mysqlTable(
  'service_pricing',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    serviceKey: serviceKeyEnum.notNull(),
    serviceName: varchar('service_name', { length: 255 }).notNull(),
    pricePerCall: decimal('price_per_call', {
      precision: 12,
      scale: 4,
    }).notNull(),
    isActive: boolean('is_active').default(true).notNull(),
    description: text('description'),
    updatedBy: bigint('updated_by', { mode: 'number' }),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    serviceKeyIdx: uniqueIndex('service_pricing_service_key_idx').on(table.serviceKey),
    updatedByFk: foreignKey({
      name: 'service_pricing_updated_by_fk',
      columns: [table.updatedBy],
      foreignColumns: [users.id],
    }).onDelete('set null'),
  })
);

export const walletTransactions = mysqlTable(
  'wallet_transactions',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    userId: bigint('user_id', { mode: 'number' }).notNull(),
    performedByUserId: bigint('performed_by_user_id', { mode: 'number' }),
    transactionType: transactionTypeEnum.notNull(),
    debitType: debitTypeEnum,
    amount: decimal('amount', { precision: 12, scale: 2 }).notNull(),
    balanceBefore: decimal('balance_before', {
      precision: 12,
      scale: 2,
    }).notNull(),
    balanceAfter: decimal('balance_after', {
      precision: 12,
      scale: 2,
    }).notNull(),
    description: text('description').notNull(),
    sourceType: sourceTypeEnum.default('USER_ACTION').notNull(),
    queryText: text('query_text'),
    metadataJson: json('metadata_json'),
    referenceEntityType: varchar('reference_entity_type', { length: 100 }),
    referenceEntityId: bigint('reference_entity_id', { mode: 'number' }),
    idempotencyKey: varchar('idempotency_key', { length: 191 }),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  table => ({
    userCreatedIdx: index('wallet_transactions_user_created_idx').on(table.userId, table.createdAt),
    debitTypeCreatedIdx: index('wallet_transactions_debit_type_created_idx').on(
      table.debitType,
      table.createdAt
    ),
    referenceIdx: index('wallet_transactions_reference_idx').on(
      table.referenceEntityType,
      table.referenceEntityId
    ),
    referenceDebitUniqueIdx: uniqueIndex('wallet_transactions_reference_debit_unique_idx').on(
      table.userId,
      table.referenceEntityType,
      table.referenceEntityId,
      table.debitType
    ),
    idempotencyIdx: uniqueIndex('wallet_transactions_idempotency_key_idx').on(table.idempotencyKey),
    userFk: foreignKey({
      name: 'wallet_transactions_user_fk',
      columns: [table.userId],
      foreignColumns: [users.id],
    }).onDelete('cascade'),
    performedByFk: foreignKey({
      name: 'wallet_transactions_performed_by_fk',
      columns: [table.performedByUserId],
      foreignColumns: [users.id],
    }).onDelete('set null'),
  })
);

export const walletRechargeOrders = mysqlTable(
  'wallet_recharge_orders',
  {
    id: bigint('id', { mode: 'number' }).primaryKey().autoincrement(),
    userId: bigint('user_id', { mode: 'number' }).notNull(),
    razorpayOrderId: varchar('razorpay_order_id', { length: 191 }).notNull(),
    razorpayPaymentId: varchar('razorpay_payment_id', { length: 191 }),
    razorpaySignature: varchar('razorpay_signature', { length: 255 }),
    amount: decimal('amount', { precision: 12, scale: 2 }).notNull(),
    currency: varchar('currency', { length: 10 }).default('INR').notNull(),
    status: walletRechargeStatusEnum.default('CREATED').notNull(),
    verifiedAt: datetime('verified_at'),
    receiptVoucherNumber: varchar('receipt_voucher_number', { length: 16 }),
    receiptVoucherIssuedAt: datetime('receipt_voucher_issued_at'),
    receiptVoucherSerial: bigint('receipt_voucher_serial', { mode: 'number' }),
    metadataJson: json('metadata_json'),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().onUpdateNow().notNull(),
  },
  table => ({
    orderIdIdx: uniqueIndex('wallet_recharge_orders_order_id_idx').on(table.razorpayOrderId),
    paymentIdIdx: uniqueIndex('wallet_recharge_orders_payment_id_idx').on(table.razorpayPaymentId),
    receiptVoucherNumberIdx: uniqueIndex('wallet_recharge_orders_receipt_voucher_number_idx').on(
      table.receiptVoucherNumber
    ),
    userCreatedIdx: index('wallet_recharge_orders_user_created_idx').on(
      table.userId,
      table.createdAt
    ),
    userFk: foreignKey({
      name: 'wallet_recharge_orders_user_fk',
      columns: [table.userId],
      foreignColumns: [users.id],
    }).onDelete('cascade'),
  })
);

// Relations
export const usersRelations = relations(users, ({ one, many }) => ({
  admin: one(users, {
    fields: [users.adminId],
    references: [users.id],
    relationName: 'managed_by_admin',
  }),
  managedUsers: many(users, { relationName: 'managed_by_admin' }),
  manager: one(users, {
    fields: [users.managerId],
    references: [users.id],
    relationName: 'managed_by_manager',
  }),
  teamMembers: many(users, { relationName: 'managed_by_manager' }),
  leads: many(leads),
  createdLeads: many(leads, { relationName: 'lead_creator' }),
  leadCategories: many(leadCategories),
  activities: many(activities),
  leadAssignmentChanges: many(leadAssignmentsHistory, {
    relationName: 'assignment_changed_by',
  }),
  leadStageChanges: many(leadStageHistory, { relationName: 'lead_stage_changed_by' }),
  leadStageHistoryForCompany: many(leadStageHistory, { relationName: 'lead_stage_company' }),
  leadPreviousAssignments: many(leadAssignmentsHistory, {
    relationName: 'assignment_previous_user',
  }),
  leadNewAssignments: many(leadAssignmentsHistory, {
    relationName: 'assignment_new_user',
  }),
  notifications: many(notifications),
  pushSubscriptions: many(pushSubscriptions),
  passwordResetRequests: many(passwordResetRequests),
  whatsappAccounts: many(whatsappAccounts),
  gmailAccounts: many(gmailAccounts),
  broadcastRecipientGroupsOwned: many(broadcastRecipientGroups, {
    relationName: 'broadcast_recipient_group_admin',
  }),
  broadcastRecipientGroupsCreated: many(broadcastRecipientGroups, {
    relationName: 'broadcast_recipient_group_creator',
  }),
  broadcastCampaignsOwned: many(broadcastCampaigns, {
    relationName: 'broadcast_campaign_admin',
  }),
  broadcastCampaignsCreated: many(broadcastCampaigns, {
    relationName: 'broadcast_campaign_creator',
  }),
  conversations: many(conversations, {
    relationName: 'conversation_admin',
  }),
  createdConversations: many(conversations, {
    relationName: 'conversation_creator',
  }),
  createdMessages: many(messages, {
    relationName: 'message_creator',
  }),
  conversationReadStates: many(conversationReadStates),
  reportSavedFilters: many(reportSavedFilters),
  leadAnalyticsSavedViews: many(leadAnalyticsSavedViews),
  settingValues: many(settingValues, { relationName: 'setting_values_owner' }),
  settingsChanges: many(settingsAuditLogs, {
    relationName: 'settings_changed_by_user',
  }),
  subscriptions: many(subscriptions),
  walletTransactions: many(walletTransactions, {
    relationName: 'wallet_transaction_user',
  }),
  walletTransactionsPerformed: many(walletTransactions, {
    relationName: 'wallet_transaction_performed_by',
  }),
  servicePricingUpdates: many(servicePricing),
  walletRechargeOrders: many(walletRechargeOrders),
}));

export const productsRelations = relations(products, ({ one, many }) => ({
  admin: one(users, {
    fields: [products.adminId],
    references: [users.id],
  }),
  leads: many(leads),
  notificationTemplates: many(notificationTemplates),
  media: many(productMedia),
}));

export const productMediaRelations = relations(productMedia, ({ one }) => ({
  product: one(products, {
    fields: [productMedia.productId],
    references: [products.id],
  }),
  admin: one(users, {
    fields: [productMedia.adminId],
    references: [users.id],
  }),
}));

export const leadCategoriesRelations = relations(leadCategories, ({ one, many }) => ({
  admin: one(users, {
    fields: [leadCategories.adminId],
    references: [users.id],
  }),
  leads: many(leads),
}));

export const leadsRelations = relations(leads, ({ one, many }) => ({
  admin: one(users, {
    fields: [leads.adminId],
    references: [users.id],
  }),
  product: one(products, {
    fields: [leads.productId],
    references: [products.id],
  }),
  category: one(leadCategories, {
    fields: [leads.categoryId],
    references: [leadCategories.id],
  }),
  assignedUser: one(users, {
    fields: [leads.assignedUserId],
    references: [users.id],
  }),
  creator: one(users, {
    fields: [leads.createdBy],
    references: [users.id],
    relationName: 'lead_creator',
  }),
  activities: many(activities),
  assignmentHistory: many(leadAssignmentsHistory),
  stageHistory: many(leadStageHistory),
  broadcastRecipients: many(broadcastRecipients),
  conversations: many(conversations),
}));

export const notificationTemplatesRelations = relations(notificationTemplates, ({ one, many }) => ({
  product: one(products, {
    fields: [notificationTemplates.productId],
    references: [products.id],
  }),
  broadcastCampaigns: many(broadcastCampaigns),
}));

export const whatsappAccountsRelations = relations(whatsappAccounts, ({ one }) => ({
  admin: one(users, {
    fields: [whatsappAccounts.adminId],
    references: [users.id],
  }),
}));

export const gmailAccountsRelations = relations(gmailAccounts, ({ one, many }) => ({
  admin: one(users, {
    fields: [gmailAccounts.adminId],
    references: [users.id],
  }),
  processedMessages: many(gmailProcessedMessages),
}));

export const gmailProcessedMessagesRelations = relations(gmailProcessedMessages, ({ one }) => ({
  account: one(gmailAccounts, {
    fields: [gmailProcessedMessages.gmailAccountId],
    references: [gmailAccounts.id],
  }),
  lead: one(leads, {
    fields: [gmailProcessedMessages.leadId],
    references: [leads.id],
  }),
}));

export const broadcastCampaignsRelations = relations(broadcastCampaigns, ({ one, many }) => ({
  admin: one(users, {
    fields: [broadcastCampaigns.adminId],
    references: [users.id],
    relationName: 'broadcast_campaign_admin',
  }),
  createdByUser: one(users, {
    fields: [broadcastCampaigns.createdByUserId],
    references: [users.id],
    relationName: 'broadcast_campaign_creator',
  }),
  template: one(notificationTemplates, {
    fields: [broadcastCampaigns.templateId],
    references: [notificationTemplates.id],
  }),
  recipients: many(broadcastRecipients),
  variables: many(broadcastCampaignVariables),
}));

export const broadcastCampaignVariablesRelations = relations(
  broadcastCampaignVariables,
  ({ one }) => ({
    campaign: one(broadcastCampaigns, {
      fields: [broadcastCampaignVariables.campaignId],
      references: [broadcastCampaigns.id],
    }),
  })
);

export const broadcastRecipientsRelations = relations(broadcastRecipients, ({ one }) => ({
  campaign: one(broadcastCampaigns, {
    fields: [broadcastRecipients.campaignId],
    references: [broadcastCampaigns.id],
  }),
  lead: one(leads, {
    fields: [broadcastRecipients.leadId],
    references: [leads.id],
  }),
}));

export const broadcastRecipientGroupsRelations = relations(
  broadcastRecipientGroups,
  ({ one, many }) => ({
    admin: one(users, {
      fields: [broadcastRecipientGroups.adminId],
      references: [users.id],
      relationName: 'broadcast_recipient_group_admin',
    }),
    createdByUser: one(users, {
      fields: [broadcastRecipientGroups.createdByUserId],
      references: [users.id],
      relationName: 'broadcast_recipient_group_creator',
    }),
    members: many(broadcastRecipientGroupMembers),
  })
);

export const broadcastRecipientGroupMembersRelations = relations(
  broadcastRecipientGroupMembers,
  ({ one }) => ({
    group: one(broadcastRecipientGroups, {
      fields: [broadcastRecipientGroupMembers.groupId],
      references: [broadcastRecipientGroups.id],
    }),
    lead: one(leads, {
      fields: [broadcastRecipientGroupMembers.leadId],
      references: [leads.id],
    }),
  })
);

export const conversationsRelations = relations(conversations, ({ one, many }) => ({
  lead: one(leads, {
    fields: [conversations.leadId],
    references: [leads.id],
  }),
  admin: one(users, {
    fields: [conversations.adminId],
    references: [users.id],
    relationName: 'conversation_admin',
  }),
  createdByUser: one(users, {
    fields: [conversations.createdByUserId],
    references: [users.id],
    relationName: 'conversation_creator',
  }),
  socialAccount: one(socialAccounts, {
    fields: [conversations.socialAccountId],
    references: [socialAccounts.id],
  }),
  messages: many(messages),
  automationJobs: many(conversationAiAutomationJobs),
  readStates: many(conversationReadStates),
}));

export const messagesRelations = relations(messages, ({ one, many }) => ({
  conversation: one(conversations, {
    fields: [messages.conversationId],
    references: [conversations.id],
  }),
  createdByUser: one(users, {
    fields: [messages.createdByUserId],
    references: [users.id],
    relationName: 'message_creator',
  }),
  automationJobs: many(conversationAiAutomationJobs),
}));

export const conversationAiAutomationJobsRelations = relations(
  conversationAiAutomationJobs,
  ({ one }) => ({
    conversation: one(conversations, {
      fields: [conversationAiAutomationJobs.conversationId],
      references: [conversations.id],
    }),
    admin: one(users, {
      fields: [conversationAiAutomationJobs.adminId],
      references: [users.id],
    }),
    inboundMessage: one(messages, {
      fields: [conversationAiAutomationJobs.inboundMessageId],
      references: [messages.id],
    }),
  })
);

export const conversationReadStatesRelations = relations(conversationReadStates, ({ one }) => ({
  conversation: one(conversations, {
    fields: [conversationReadStates.conversationId],
    references: [conversations.id],
  }),
  user: one(users, {
    fields: [conversationReadStates.userId],
    references: [users.id],
  }),
}));

export const conversationAutomationStatesRelations = relations(
  conversationAutomationStates,
  ({ one }) => ({
    conversation: one(conversations, {
      fields: [conversationAutomationStates.conversationId],
      references: [conversations.id],
    }),
    admin: one(users, {
      fields: [conversationAutomationStates.adminId],
      references: [users.id],
    }),
    updatedByUser: one(users, {
      fields: [conversationAutomationStates.updatedByUserId],
      references: [users.id],
    }),
  })
);

export const activitiesRelations = relations(activities, ({ one }) => ({
  lead: one(leads, {
    fields: [activities.leadId],
    references: [leads.id],
  }),
  user: one(users, {
    fields: [activities.userId],
    references: [users.id],
  }),
}));

export const pushSubscriptionsRelations = relations(pushSubscriptions, ({ one }) => ({
  user: one(users, {
    fields: [pushSubscriptions.userId],
    references: [users.id],
  }),
}));

export const subscriptionsRelations = relations(subscriptions, ({ one }) => ({
  user: one(users, {
    fields: [subscriptions.userId],
    references: [users.id],
  }),
}));

export const servicePricingRelations = relations(servicePricing, ({ one }) => ({
  updatedByUser: one(users, {
    fields: [servicePricing.updatedBy],
    references: [users.id],
  }),
}));

export const walletTransactionsRelations = relations(walletTransactions, ({ one }) => ({
  user: one(users, {
    fields: [walletTransactions.userId],
    references: [users.id],
    relationName: 'wallet_transaction_user',
  }),
  performedByUser: one(users, {
    fields: [walletTransactions.performedByUserId],
    references: [users.id],
    relationName: 'wallet_transaction_performed_by',
  }),
}));

export const walletRechargeOrdersRelations = relations(walletRechargeOrders, ({ one }) => ({
  user: one(users, {
    fields: [walletRechargeOrders.userId],
    references: [users.id],
  }),
}));

export const leadAssignmentsHistoryRelations = relations(leadAssignmentsHistory, ({ one }) => ({
  lead: one(leads, {
    fields: [leadAssignmentsHistory.leadId],
    references: [leads.id],
  }),
  previousAssignedUser: one(users, {
    fields: [leadAssignmentsHistory.previousAssignedUserId],
    references: [users.id],
    relationName: 'assignment_previous_user',
  }),
  newAssignedUser: one(users, {
    fields: [leadAssignmentsHistory.newAssignedUserId],
    references: [users.id],
    relationName: 'assignment_new_user',
  }),
  changedByUser: one(users, {
    fields: [leadAssignmentsHistory.changedBy],
    references: [users.id],
    relationName: 'assignment_changed_by',
  }),
}));

export const leadStageHistoryRelations = relations(leadStageHistory, ({ one }) => ({
  lead: one(leads, {
    fields: [leadStageHistory.leadId],
    references: [leads.id],
  }),
  company: one(users, {
    fields: [leadStageHistory.adminId],
    references: [users.id],
    relationName: 'lead_stage_company',
  }),
  changedByUser: one(users, {
    fields: [leadStageHistory.changedByUserId],
    references: [users.id],
    relationName: 'lead_stage_changed_by',
  }),
}));

export const passwordResetRequestsRelations = relations(passwordResetRequests, ({ one }) => ({
  user: one(users, {
    fields: [passwordResetRequests.userId],
    references: [users.id],
  }),
  lastNotification: one(notificationQueue, {
    fields: [passwordResetRequests.lastNotificationId],
    references: [notificationQueue.id],
  }),
}));

export const reportSavedFiltersRelations = relations(reportSavedFilters, ({ one }) => ({
  user: one(users, {
    fields: [reportSavedFilters.userId],
    references: [users.id],
  }),
}));

export const leadAnalyticsSavedViewsRelations = relations(leadAnalyticsSavedViews, ({ one }) => ({
  user: one(users, {
    fields: [leadAnalyticsSavedViews.userId],
    references: [users.id],
  }),
}));

export const settingValuesRelations = relations(settingValues, ({ one }) => ({
  company: one(users, {
    fields: [settingValues.companyId],
    references: [users.id],
    relationName: 'setting_values_owner',
  }),
  updatedByUser: one(users, {
    fields: [settingValues.updatedBy],
    references: [users.id],
  }),
}));

export const settingsAuditLogsRelations = relations(settingsAuditLogs, ({ one }) => ({
  company: one(users, {
    fields: [settingsAuditLogs.companyId],
    references: [users.id],
  }),
  changedByUser: one(users, {
    fields: [settingsAuditLogs.changedBy],
    references: [users.id],
    relationName: 'settings_changed_by_user',
  }),
}));

export const localeSchema = z.enum(supportedLocales);

const normalizeCompanyNameInput = (value: unknown) => {
  if (typeof value !== 'string') {
    return value;
  }

  return value.trim().replace(/\s+/g, ' ');
};

export const notificationTemplateLanguageSchema = z
  .string()
  .trim()
  .min(1)
  .max(20)
  .transform(value => normalizeSupportedLocale(value) ?? value);

const normalizeTimeZonePreferenceInput = (value: unknown) => {
  if (value === '') {
    return null;
  }

  return typeof value === 'string' ? value.trim() : value;
};

const timeZonePreferenceSchema = z.preprocess(
  normalizeTimeZonePreferenceInput,
  z
    .union([
      z
        .string()
        .min(1)
        .max(64)
        .refine(value => {
          try {
            new Intl.DateTimeFormat('en-US', { timeZone: value }).format(new Date());
            return true;
          } catch (_error) {
            return false;
          }
        }, 'Invalid time zone'),
      z.null(),
    ])
    .optional()
);

export const userPreferencesSchema = z.object({
  preferredLocale: z.union([localeSchema, z.null()]).optional(),
  timeZone: timeZonePreferenceSchema,
});

// Schemas
export const insertUserSchema = createInsertSchema(users)
  .omit({
    id: true,
    walletBalance: true,
    walletLastUpdatedAt: true,
    createdAt: true,
  })
  .extend({
    preferredLocale: z.preprocess(
      value => {
        if (value === '') {
          return null;
        }

        if (typeof value !== 'string') {
          return value;
        }

        return normalizeSupportedLocale(value);
      },
      z.union([localeSchema, z.null()]).optional()
    ),
    timeZone: timeZonePreferenceSchema,
    roles: z.array(z.enum(userCapabilities)).nullable().optional(),
    email: z.preprocess(
      value => {
        if (typeof value !== 'string') {
          return value;
        }

        return value.trim().toLowerCase();
      },
      z
        .string({
          required_error: 'Email is required',
          invalid_type_error: 'Email is required',
        })
        .trim()
        .min(1, 'Email is required')
        .email('Invalid email address')
    ),
    phone: z.preprocess(
      value => {
        if (typeof value !== 'string') return value;

        const normalized = value.trim().replace(/\s+/g, ' ');
        if (isPhoneValueEmptyLike(normalized)) {
          return '';
        }

        const [countryCode = '', ...phoneParts] = normalized.split(' ');
        return combinePhoneNumber(countryCode, phoneParts.join(' '));
      },
      z
        .string({
          required_error: PHONE_NUMBER_ERROR_MESSAGE,
          invalid_type_error: PHONE_NUMBER_ERROR_MESSAGE,
        })
        .trim()
        .min(1, PHONE_NUMBER_ERROR_MESSAGE)
        .superRefine((value: string, ctx: z.RefinementCtx) => {
          if (!isPhoneValueWithCountryCode(value)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: PHONE_NUMBER_ERROR_MESSAGE,
            });
          }
        })
    ),
  });
export const publicSignupSchema = insertUserSchema
  .pick({
    username: true,
    password: true,
    name: true,
    email: true,
    phone: true,
    preferredLocale: true,
    timeZone: true,
  })
  .extend({
    companyName: z.preprocess(
      normalizeCompanyNameInput,
      z
        .string({
          required_error: 'Company name is required',
          invalid_type_error: 'Company name is required',
        })
        .trim()
        .min(2, 'Company name must be at least 2 characters')
        .max(255, 'Company name must be 255 characters or fewer')
    ),
    industry: z.enum(companyIndustries, {
      required_error: 'Industry is required',
      invalid_type_error: 'Industry is required',
    }),
    industryDescription: z.preprocess(value => {
      if (value == null || value === '') {
        return null;
      }

      if (typeof value !== 'string') {
        return value;
      }

      const normalized = value.trim().replace(/\s+/g, ' ');
      return normalized.length > 0 ? normalized : null;
    }, z.string().trim().max(240, 'Industry description must be 240 characters or fewer.').nullable().optional()),
  })
  .superRefine(
    (
      value: {
        industry: CompanyIndustry;
        industryDescription?: string | null;
      },
      ctx: z.RefinementCtx
    ) => {
      if (value.industry === 'Other' && !value.industryDescription?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['industryDescription'],
          message: 'Industry description is required when Other is selected.',
        });
      }
    }
  );
const optionalProductTextSchema = (maxLength: number) =>
  z.preprocess(
    value => (typeof value === 'string' && value.trim() === '' ? null : value),
    z.string().trim().max(maxLength).nullable().optional()
  );

const optionalProductUrlSchema = z.preprocess(
  value => (typeof value === 'string' && value.trim() === '' ? null : value),
  z.string().trim().url().max(2048).nullable().optional()
);

// Base object schema for product writes. `insertProductSchema` wraps it in `superRefine`, which
// returns a ZodEffects without `.extend()`, `.omit()`, or `.partial()`; derive form and import
// schemas from this object schema instead.
export const productInputSchema = createInsertSchema(products)
  .omit({
    archivedAt: true,
    archivedBy: true,
    id: true,
    adminId: true,
    sku: true,
    externalId: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    name: z.string().trim().min(1, 'Product name is required').max(500),
    description: optionalProductTextSchema(10000),
    brand: optionalProductTextSchema(160),
    category: optionalProductTextSchema(160),
    currency: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z]{3}$/)
      .default('INR'),
    productUrl: optionalProductUrlSchema,
    inventoryQuantity: z.number().int().min(0).max(2_147_483_647).nullable().optional(),
  });

export function normalizeProductInventory<
  T extends { availability?: string; inventoryQuantity?: number | null },
>(value: T): T {
  return value.availability === 'out_of_stock' ? { ...value, inventoryQuantity: 0 } : value;
}

export function validateProductInventory(
  value: { type?: string; availability?: string; inventoryQuantity?: number | null },
  context: z.RefinementCtx
) {
  if (
    value.type !== 'service' &&
    ['in_stock', 'available'].includes(value.availability ?? 'in_stock') &&
    (value.inventoryQuantity == null || value.inventoryQuantity <= 0)
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['inventoryQuantity'],
      message: 'Enter an inventory quantity greater than zero.',
    });
  }
}

const validateProductNegotiationPrices = (
  value: {
    negotiationMinPrice?: number | null;
    negotiationMaxPrice?: number | null;
    priceMode?: string | null;
    price?: number | null;
    salePrice?: number | null;
  },
  context: z.RefinementCtx
) => {
  const minPrice = value.negotiationMinPrice;
  const maxPrice = value.negotiationMaxPrice;

  if ((minPrice == null) !== (maxPrice == null)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['negotiationMinPrice'],
      message: 'Enter a lowest acceptable price and its listed-price ceiling, or leave both blank.',
    });
    return;
  }

  if (minPrice == null || maxPrice == null) {
    return;
  }

  if (value.priceMode === 'fixed') {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['negotiationMinPrice'],
      message: 'Fixed-price products cannot have a negotiation range. Choose negotiable pricing.',
    });
    return;
  }

  if (minPrice <= 0 || maxPrice <= 0 || minPrice > maxPrice) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['negotiationMaxPrice'],
      message:
        'Negotiation prices must be positive and the lowest acceptable price cannot exceed the listed price.',
    });
  }

  const listedPrice = value.salePrice ?? value.price;
  if (listedPrice != null && maxPrice > listedPrice) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['negotiationMaxPrice'],
      message: 'The negotiation ceiling cannot exceed the listed product price.',
    });
  }
};

export const insertProductSchema = productInputSchema
  .transform(normalizeProductInventory)
  .superRefine(validateProductInventory)
  .superRefine(validateProductNegotiationPrices);

export const insertProductUpdateSchema = productInputSchema
  .partial()
  .transform(normalizeProductInventory)
  .superRefine(validateProductNegotiationPrices);
export const insertLeadCategorySchema = createInsertSchema(leadCategories)
  .omit({
    id: true,
    adminId: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    name: z.string().trim().min(1, 'Category name is required').max(100),
  });
const dateOnlyInputPattern = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseDateOnlyInputAsLocalDate = (value: string) => {
  const match = dateOnlyInputPattern.exec(value);

  if (!match) {
    return value;
  }

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0, 0);

  return date.getFullYear() === Number(year) &&
    date.getMonth() === Number(month) - 1 &&
    date.getDate() === Number(day)
    ? date
    : value;
};

const nextFollowupDateSchema = z.preprocess(
  value => {
    if (value === '') {
      return undefined;
    }

    return typeof value === 'string' ? parseDateOnlyInputAsLocalDate(value.trim()) : value;
  },
  z
    .union([z.string(), z.date()])
    .pipe(z.coerce.date().max(LEAD_FOLLOWUP_MAX_TIMESTAMP, LEAD_FOLLOWUP_MAX_DATE_MESSAGE))
    .optional()
    .nullable()
);

const nextFollowupPurposeSchema = z.preprocess(
  value => (value === '' ? undefined : value),
  z.enum(leadFollowupPurposes).optional().nullable()
);

const nextFollowupDurationMinutesSchema = z.preprocess(
  value => (value === '' ? undefined : value),
  z.coerce
    .number()
    .int()
    .min(LEAD_FOLLOWUP_MIN_DURATION_MINUTES)
    .max(LEAD_FOLLOWUP_MAX_DURATION_MINUTES)
    .optional()
    .nullable()
);

const normalizeRequiredTextInput = (value: unknown) => {
  if (typeof value !== 'string') {
    return value;
  }

  return value.trim().replace(/\s+/g, ' ');
};

export const insertLeadSchema = createInsertSchema(leads)
  .omit({
    inventoryConsumedProductId: true,
    productSnapshot: true,
    id: true,
    leadCode: true,
    createdAt: true,
    updatedAt: true,
    googleWorkspaceImported: true,
  })
  .extend({
    name: z.preprocess(
      normalizeRequiredTextInput,
      z
        .string({
          required_error: 'Lead name is required',
          invalid_type_error: 'Lead name is required',
        })
        .min(1, 'Lead name is required')
    ),
    nextFollowupDate: nextFollowupDateSchema,
    nextFollowupPurpose: nextFollowupPurposeSchema,
    nextFollowupDurationMinutes: nextFollowupDurationMinutesSchema,
    categoryId: z.coerce.number().int().positive().nullable().optional(),
    phone: z.preprocess(
      value => {
        if (typeof value !== 'string') return value;

        return value.trim().replace(/\s+/g, ' ');
      },
      z
        .string()
        .trim()
        .superRefine((value: string, ctx: z.RefinementCtx) => {
          if (!isPhoneValueWithCountryCode(value)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: PHONE_NUMBER_ERROR_MESSAGE,
            });
          }
        })
    ),
  });
export const insertActivitySchema = createInsertSchema(activities)
  .omit({ id: true, createdAt: true })
  .extend({
    scheduledAt: z
      .union([z.string().pipe(z.coerce.date()), z.date()])
      .optional()
      .nullable(),
  });
export const insertLeadAssignmentHistorySchema = createInsertSchema(leadAssignmentsHistory).omit({
  id: true,
  changedAt: true,
});
export const insertLeadStageHistorySchema = createInsertSchema(leadStageHistory).omit({
  id: true,
  changedAt: true,
});
export const insertNotificationSchema = createInsertSchema(notifications).omit({
  id: true,
  createdAt: true,
});
export const insertPushSubscriptionSchema = createInsertSchema(pushSubscriptions).omit({
  id: true,
  endpointHash: true,
  createdAt: true,
  updatedAt: true,
  lastSuccessAt: true,
  lastFailureAt: true,
  failureReason: true,
});
export const insertNotificationTemplateSchema = createInsertSchema(notificationTemplates)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    language: z.preprocess(value => {
      if (typeof value !== 'string') {
        return value;
      }

      return normalizeSupportedLocale(value) ?? value.trim();
    }, z.string().trim().min(1).max(20).optional()),
  });
export const insertWhatsAppAccountSchema = createInsertSchema(whatsappAccounts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertGmailAccountSchema = createInsertSchema(gmailAccounts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertBroadcastCampaignSchema = createInsertSchema(broadcastCampaigns)
  .omit({
    id: true,
    totalRecipients: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    scheduledAt: z
      .union([z.string().pipe(z.coerce.date()), z.date()])
      .optional()
      .nullable(),
  });
export const insertBroadcastRecipientSchema = createInsertSchema(broadcastRecipients).omit({
  id: true,
  sentAt: true,
  errorMessage: true,
  providerMessageId: true,
  createdAt: true,
  updatedAt: true,
});
export const insertBroadcastCampaignVariableSchema = createInsertSchema(
  broadcastCampaignVariables
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertBroadcastRecipientGroupSchema = createInsertSchema(
  broadcastRecipientGroups
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertBroadcastRecipientGroupMemberSchema = createInsertSchema(
  broadcastRecipientGroupMembers
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertConversationSchema = createInsertSchema(conversations)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .extend({
    lastMessageAt: z
      .union([z.string().pipe(z.coerce.date()), z.date()])
      .optional()
      .nullable(),
  });
export const insertMessageSchema = createInsertSchema(messages).omit({
  id: true,
  createdAt: true,
});
export const insertConversationReadStateSchema = createInsertSchema(conversationReadStates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertConversationAutomationStateSchema = createInsertSchema(
  conversationAutomationStates
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertConversationAiAutomationJobSchema = createInsertSchema(
  conversationAiAutomationJobs
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertNotificationQueueSchema = createInsertSchema(notificationQueue).omit({
  id: true,
  processingStartedAt: true,
  processingToken: true,
  sentAt: true,
  providerMessageId: true,
  providerResponse: true,
  failureReason: true,
  createdAt: true,
  updatedAt: true,
});
export const insertNotificationDeliveryAttemptSchema = createInsertSchema(
  notificationDeliveryAttempts
).omit({
  id: true,
  createdAt: true,
});
export const insertPasswordResetRequestSchema = createInsertSchema(passwordResetRequests).omit({
  id: true,
  verifiedAt: true,
  consumedAt: true,
  createdAt: true,
  updatedAt: true,
});
export const insertReportSavedFilterSchema = createInsertSchema(reportSavedFilters).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertLeadAnalyticsSavedViewSchema = createInsertSchema(leadAnalyticsSavedViews).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertSettingValueSchema = createInsertSchema(settingValues).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertSettingsAuditLogSchema = createInsertSchema(settingsAuditLogs).omit({
  id: true,
  createdAt: true,
});
export const insertSubscriptionSchema = createInsertSchema(subscriptions)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    startDate: z
      .union([z.string().pipe(z.coerce.date()), z.date()])
      .optional()
      .nullable(),
    expiryDate: z
      .union([z.string().pipe(z.coerce.date()), z.date()])
      .optional()
      .nullable(),
  });
export const insertServicePricingSchema = createInsertSchema(servicePricing).omit({
  id: true,
  updatedAt: true,
});
export const insertWalletTransactionSchema = createInsertSchema(walletTransactions).omit({
  id: true,
  createdAt: true,
});
export const insertWalletRechargeOrderSchema = createInsertSchema(walletRechargeOrders).omit({
  id: true,
  verifiedAt: true,
  createdAt: true,
  updatedAt: true,
});

// Contact messages table
export const contactMessages = mysqlTable('contact_messages', {
  id: int('id').primaryKey().autoincrement(),
  name: varchar('name', { length: 255 }).notNull(),
  email: varchar('email', { length: 255 }).notNull(),
  phone: varchar('phone', { length: 50 }),
  subject: varchar('subject', { length: 255 }).notNull(),
  message: text('message').notNull(),
  status: mysqlEnum('status', ['new', 'in_progress', 'resolved']).default('new').notNull(),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// Contact message validation schema
export const insertContactMessageSchema = createInsertSchema(contactMessages).extend({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.preprocess(
    value => {
      if (typeof value !== 'string') return value;

      const normalized = value.trim().replace(/\s+/g, ' ');
      return isPhoneValueEmptyLike(normalized) ? undefined : normalized;
    },
    z
      .string()
      .trim()
      .superRefine((value: string, ctx: z.RefinementCtx) => {
        if (!isPhoneValueWithCountryCode(value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: PHONE_NUMBER_ERROR_MESSAGE,
          });
        }
      })
      .nullable()
      .optional()
  ),
  subject: z
    .string()
    .trim()
    .min(3, 'Subject must be at least 3 characters')
    .regex(/^[^\r\n]+$/, 'Subject cannot contain line breaks'),
  message: z.string().min(10, 'Message must be at least 10 characters'),
});

// Types
export type User = typeof users.$inferSelect;
export type CompanyIndustry = (typeof companyIndustries)[number];
export type InsertUser = z.infer<typeof insertUserSchema>;
export type PublicSignupInput = z.infer<typeof publicSignupSchema>;
export type Product = typeof products.$inferSelect;
export type InsertProduct = typeof products.$inferInsert;
export type ProductMedia = typeof productMedia.$inferSelect;
export type ProductIntegrationKey = typeof productIntegrationKeys.$inferSelect;
export type LeadCategory = typeof leadCategories.$inferSelect;
export type InsertLeadCategory = typeof leadCategories.$inferInsert;
export type Lead = typeof leads.$inferSelect;
export type InsertLead = z.infer<typeof insertLeadSchema>;
export type Activity = typeof activities.$inferSelect;
export type ActivitySource = (typeof activitySources)[number];
export type InsertActivity = z.infer<typeof insertActivitySchema>;
export type LeadAssignmentHistory = typeof leadAssignmentsHistory.$inferSelect;
export type InsertLeadAssignmentHistory = z.infer<typeof insertLeadAssignmentHistorySchema>;
export type LeadStageHistory = typeof leadStageHistory.$inferSelect;
export type InsertLeadStageHistory = z.infer<typeof insertLeadStageHistorySchema>;
export type Notification = typeof notifications.$inferSelect;
export type InsertNotification = z.infer<typeof insertNotificationSchema>;
export type PushSubscriptionRecord = typeof pushSubscriptions.$inferSelect;
export type InsertPushSubscription = z.infer<typeof insertPushSubscriptionSchema>;
export type NotificationTemplate = typeof notificationTemplates.$inferSelect;
export type InsertNotificationTemplate = z.infer<typeof insertNotificationTemplateSchema>;
export type WhatsAppAccount = typeof whatsappAccounts.$inferSelect;
export type InsertWhatsAppAccount = z.infer<typeof insertWhatsAppAccountSchema>;
export type GmailAccount = typeof gmailAccounts.$inferSelect;
export type InsertGmailAccount = z.infer<typeof insertGmailAccountSchema>;
export type BroadcastCampaign = typeof broadcastCampaigns.$inferSelect;
export type InsertBroadcastCampaign = z.infer<typeof insertBroadcastCampaignSchema>;
export type BroadcastRecipient = typeof broadcastRecipients.$inferSelect;
export type InsertBroadcastRecipient = z.infer<typeof insertBroadcastRecipientSchema>;
export type BroadcastCampaignVariable = typeof broadcastCampaignVariables.$inferSelect;
export type InsertBroadcastCampaignVariable = z.infer<typeof insertBroadcastCampaignVariableSchema>;
export type BroadcastRecipientGroup = typeof broadcastRecipientGroups.$inferSelect;
export type InsertBroadcastRecipientGroup = z.infer<typeof insertBroadcastRecipientGroupSchema>;
export type BroadcastRecipientGroupMember = typeof broadcastRecipientGroupMembers.$inferSelect;
export type InsertBroadcastRecipientGroupMember = z.infer<
  typeof insertBroadcastRecipientGroupMemberSchema
>;
export type Conversation = typeof conversations.$inferSelect;
export type InsertConversation = z.infer<typeof insertConversationSchema>;
export type ConversationMessage = typeof messages.$inferSelect;
export type InsertConversationMessage = z.infer<typeof insertMessageSchema>;
export type ConversationAutomationState = typeof conversationAutomationStates.$inferSelect;
export type InsertConversationAutomationState = z.infer<
  typeof insertConversationAutomationStateSchema
>;
export type ConversationAiAutomationJob = typeof conversationAiAutomationJobs.$inferSelect;
export type InsertConversationAiAutomationJob = z.infer<
  typeof insertConversationAiAutomationJobSchema
>;
export type AiSocialPost = typeof aiSocialPosts.$inferSelect;
export type AiGenerationOperation = typeof aiGenerationOperations.$inferSelect;
export type InsertAiGenerationOperation = typeof aiGenerationOperations.$inferInsert;
export type ConversationReadState = typeof conversationReadStates.$inferSelect;
export type InsertConversationReadState = z.infer<typeof insertConversationReadStateSchema>;
export type NotificationQueueItem = typeof notificationQueue.$inferSelect;
export type InsertNotificationQueueItem = z.infer<typeof insertNotificationQueueSchema>;
export type NotificationDeliveryAttempt = typeof notificationDeliveryAttempts.$inferSelect;
export type InsertNotificationDeliveryAttempt = z.infer<
  typeof insertNotificationDeliveryAttemptSchema
>;
export type PasswordResetRequest = typeof passwordResetRequests.$inferSelect;
export type InsertPasswordResetRequest = z.infer<typeof insertPasswordResetRequestSchema>;
export type ReportSavedFilter = typeof reportSavedFilters.$inferSelect;
export type InsertReportSavedFilter = z.infer<typeof insertReportSavedFilterSchema>;
export type LeadAnalyticsSavedView = typeof leadAnalyticsSavedViews.$inferSelect;
export type InsertLeadAnalyticsSavedView = z.infer<typeof insertLeadAnalyticsSavedViewSchema>;
export type SettingValue = typeof settingValues.$inferSelect;
export type InsertSettingValue = z.infer<typeof insertSettingValueSchema>;
export type SettingsAuditLog = typeof settingsAuditLogs.$inferSelect;
export type InsertSettingsAuditLog = z.infer<typeof insertSettingsAuditLogSchema>;
export type Subscription = typeof subscriptions.$inferSelect;
export type InsertSubscription = z.infer<typeof insertSubscriptionSchema>;
export type ServicePricing = typeof servicePricing.$inferSelect;
export type InsertServicePricing = z.infer<typeof insertServicePricingSchema>;
export type WalletTransaction = typeof walletTransactions.$inferSelect;
export type InsertWalletTransaction = z.infer<typeof insertWalletTransactionSchema>;
export type WalletRechargeOrder = typeof walletRechargeOrders.$inferSelect;
export type InsertWalletRechargeOrder = z.infer<typeof insertWalletRechargeOrderSchema>;
export type ContactMessage = typeof contactMessages.$inferSelect;
export type InsertContactMessage = z.infer<typeof insertContactMessageSchema>;
